// THE telehealth customer vocabulary — the only place these words may exist.
//
// A sibling of portal/display_strings.ts rather than a `vertical` parameter on
// it: that module's own header forbids conditional wording ("1:1, no
// conditional wording"), and a branch inside it would be exactly that. Two
// verticals, two 1:1 tables, each independently counsel-reviewable. The lint
// gate (scripts/check-display-strings.mjs) scans both trees.
//
// The med-spa strings cannot be reused directly: they are per-CELL and
// state-shaped ("Not permitted in this state"), while a telehealth finding is
// (prescriber x medication x state) and its failure is almost always about a
// credential rather than a prohibition.

// Type-only: `shell/sections.ts` imports `SHELL_SECTION_LABEL` from THIS
// file, so a value import here would be a runtime circular dependency. A
// `import type` is erased at compile time and carries none of that risk --
// used only to type an action's destination rail section (`describeUnassessed`,
// `UnassessedReasonInfo`) in the same vocabulary the rail itself uses.
import type { SectionKey } from './shell/sections';
import { formatCalendarDate, formatViewerDate } from '../shared/date.ts';
import { HELD_SENTENCE, HELD_WORDS } from '../shared/heldWords.ts';

export type EngineVerdict = 'compliant' | 'non_compliant' | 'grey';

export const SUPPORT_REPORT_WORDS = {
  action: "This doesn't look right",
  title: "Tell us what looks wrong",
  note: "Short note",
  findingId: "Finding ID (optional)",
  noteHint: "Do not include patient names, health details, or identifiers.",
  submit: "Send report",
  sending: "Sending…",
  cancel: "Cancel",
  sent: "Report sent to the VerityRx support team.",
  failed: "The report could not be sent. Try again or use the support contact in the page footer.",
} as const;

export const TX_WORKFLOW_WORDS = {
  open: 'Different for this row?',
  rowContext: 'Texas answers for {prescriber} · {state} · {medication}',
  closeOverride: 'Close Texas answers',
  scope: 'These answers override your Texas practice answers for this combination. Do not enter patient details.',
  inherit: 'Use practice answer',
  relationship: 'How is the clinician–patient relationship established?',
  followup: 'For a new remote relationship, is the required follow-up process in place?',
  chronic: 'Does this combination treat chronic pain?',
  established: 'Do audio-only repeats occur only for established pain patients of this prescriber?',
  identical: 'Are audio-only repeat prescriptions identical to the previous prescription?',
  visit90: 'Do audio-only repeats require a qualifying in-person or two-way video visit within 90 days?',
  documented: 'Does the APRN document each exception and the reason for the remote visit?',
  factors: 'Does the APRN consider the last in-person visit, coexisting conditions, and work-related COVID risks?',
  save: 'Save Texas visit answers',
  unsure: 'Not sure',
  yes: 'Yes',
  no: 'No',
  preexisting: 'Existing clinician–patient relationship',
  callCoverage: 'Qualifying call coverage',
  storeForward: 'Store-and-forward with relevant clinical information',
  noRelationship: 'None of these',
} as const;

/** Which kind of unresolved — i.e. WHO can close it. Server-derived. */
export type GreyKind =
  | 'input_missing'
  | 'awaiting_your_answer'
  | 'awaiting_confirmation'
  | 'counsel_decides'
  | 'not_yet_reviewed_by_us';

/**
 * The verdict word.
 *
 * `non_compliant` renders as "Requirement not met" — never "Non-compliant",
 * never "Violation". The engine tests whether the supplied records establish a
 * requirement; it does not find that a law was broken, and a customer showing
 * this to an investor must not be made to say something stronger than we mean.
 */
export function verdictLabel(
  verdict: EngineVerdict,
  grey?: GreyKind | null,
): string {
  if (verdict === 'compliant') return 'Meets requirements';
  if (verdict === 'non_compliant') return 'Requirement not met';
  switch (grey) {
    // "Needs your confirmation" / "For your lawyer to decide" (2026-09-23):
    // parallel to "Needs your answer", and the plain word a practice owner
    // uses -- "counsel" read as our jargon on the results page.
    case 'awaiting_confirmation':
      return 'Needs your confirmation';
    case 'awaiting_your_answer':
      return 'Needs your answer';
    case 'input_missing':
      // TODO(Shawn): this grey is closed by SENDING A DOCUMENT (greyAsk), the
      // table's word for it is "Needs your answer". Same owner (the practice),
      // different act; the hint under it still says "send".
      return 'Needs your answer';
    case 'counsel_decides':
      return 'Needs a legal judgement';
    default:
      // Ours, not the state's. Saying "unresolved in law" about something
      // nobody has read asserts a fact about the state we never checked.
      return 'Not yet reviewed by us';
  }
}

/** Who can close this — rendered as a small achromatic label, never a colour. */
export function closability(
  verdict: EngineVerdict,
  grey?: GreyKind | null,
): string | null {
  if (verdict !== 'grey') return null;
  switch (grey) {
    case 'awaiting_confirmation':
      return 'You can confirm this';
    case 'awaiting_your_answer':
      return 'You can answer this';
    case 'input_missing':
      return 'You can send this';
    case 'counsel_decides':
      return 'Take this to your lawyer';
    default:
      return "We're still working on this";
  }
}

/** One sentence per unresolved kind: the ask, in the customer's terms. */
export function greyAsk(grey: GreyKind): string {
  switch (grey) {
    case 'awaiting_confirmation':
      return 'Your records cannot show how you handle this. Confirm what your practice does; we have not marked it as a failure.';
    case 'awaiting_your_answer':
      return 'We do not have an answer about this prescriber’s required credential. Answer under Still needed; we will check again.';
    case 'input_missing':
      return 'Send the missing document under Send us files. We will check again after it is received.';
    case 'counsel_decides':
      return 'Take this to your lawyer.';
    default:
      return 'We have not reviewed this state requirement yet. You do not need to answer this item.';
  }
}

export const VERDICT_CLASS: Record<EngineVerdict, string> = {
  compliant: 'ok',
  non_compliant: 'no',
  grey: 'und',
};

/** Whose word a credential rests on. Row 1 renders nowhere today (BL-174). */
export function evidenceChip(
  evidence: string,
  boardVerified: boolean,
  corroborations: number,
): string {
  if (boardVerified) return 'Confirmed with the state board';
  if (evidence === 'primary_source') return 'Official record — you supplied it';
  if (evidence === 'manual_upload') return 'Your document';
  if (corroborations >= 1) return 'Your records, matched across two files';
  return 'Not used — your word only';
}

/** The hover text for each rung. Every non-board rung ends the same way. */
export function evidenceDetail(
  evidence: string,
  boardVerified: boolean,
  corroborations: number,
): string {
  if (boardVerified) {
    return "We checked this against the issuing board's own record.";
  }
  const tail = ' We did not query the board.';
  if (evidence === 'primary_source')
    return (
      'Read from an official verification page you sent us, showing the status printed on it.' +
      tail
    );
  if (evidence === 'manual_upload')
    return (
      'Read from the credential document you supplied, which we have not confirmed is current.' +
      tail
    );
  if (corroborations >= 1)
    return (
      'You told us this and it appears independently in at least two of the files you sent.' +
      tail
    );
  return 'You told us this and nothing else in your records confirms it. It was not used to decide any result here.';
}

/** Shown under the CREDENTIAL list, which is what it is about.
 *
 *  It used to sit at the foot of the binder tab, where "these" had no referent
 *  and it read as a disclaimer over the whole analysis. It is a specific point
 *  about specific facts: the licences below are your statements, we did not
 *  check them with any board, and a wrong one invalidates what rests on it. */
export const ASSERTION_CAVEAT =
  'These results depend on the credentials below being current and accurate. We have not checked them with a licensing board. If one is wrong or expired, the results that depend on it do not hold.';

/** The binder tab, when there is no PDF to download yet. */
export const BINDER_NOT_YET =
  'No signed report is ready. Once your analysis is complete, counsel reviews and signs the final version. The figures here come from that same analysis.';

/** How to reach a person. A compliance surface with no way to reply is a
 *  surface that tells you about your problems and refuses to discuss them. */
/** How to reach a person.
 *
 *  The address is passed in, never written here. This first shipped naming
 *  `support@verityrx.com`, which existed nowhere but that sentence — a
 *  practice writing to it would have been talking to no one, on the one screen
 *  whose entire point is "tell us if this is wrong". With no address
 *  configured it says the thing that is always true instead. */
export function contactNote(supportEmail?: string | null): string {
  // The same two sentences as the med-spa footer (COHERE2 P2-6): a question,
  // and where to send it. Files go under "Send us files" on the page itself.
  return (
    'Something wrong, or a question? ' +
    (supportEmail
      ? `Write to us at ${supportEmail}.`
      : 'Reply to the email that brought you here.')
  );
}

/** The permanent frame. A completed round is NOT the signed deliverable. */
export function reviewBanner(state: string, outcome: string | null): string {
  if (state === 'round_completed') {
    // BL-892: a round counsel closed with a request for changes is not
    // "complete" to the practice; it reads what counsel did and what follows.
    if (outcome === 'changes_requested') {
      return 'Counsel asked for changes to this version, so a newer version is being prepared. A review round is not the signed report — that is a separate document.';
    }
    // Arvind 2026-09-29: never "Your counsel ..." unless counsel did it, and
    // this legacy frame cannot tell (the team may record counsel's outcome).
    const what = outcome === 'signed_off' ? 'recorded as "signed off"' : 'recorded';
    return `A review round was ${what} on this version. A completed review round is not the signed report — that is a separate document.`;
  }
  return 'This is our analysis of your own data. No attorney has reviewed it yet.';
}

/** The answer to "does my lawyer see this same screen?", rendered, not buried.
 *
 *  Rewritten after a reader took the original to mean their attorney could see
 *  their confirmations and their attorney's notes were on this page. Both are
 *  false and the second one matters: their counsel's reasoning is not merely
 *  hidden here, the database refuses to hand it to this surface. So the
 *  paragraph now says which direction each thing travels. */
export const WHO_SEES_WHAT =
  'When your report is sent to counsel, they can review the same findings in a separate workspace. They see every requirement we tested and can accept, reject, or defer each finding. They do not see this page, and their notes and decisions are not shown here. Your answers are saved with your practice details.';

/** Counted per prescriber, per medication, per state — said out loud. */
export const COUNTING_NOTE =
  'Each prescriber–medication–state combination is one check. One missing document can affect many checks. A requirement not met is based on the records supplied; it is not a finding that a law was broken.';

export const NOT_ASSESSED_NOTE =
  'We have not cleared activity we could not check.';

export const FOOTER_DISCLAIMER =
  'This is not legal advice. For legal advice about your practice, consult your counsel.';

// --------------------------------------------------------------------------- //
// Confirmations
// --------------------------------------------------------------------------- //
// Signing records the practice's statement for counsel. Keep that receipt
// distinct from the engine's status for any affected check.

/** The status of one requirement in the ledger. Never a verdict word. */
export function confirmationStatus(status: string): string {
  switch (status) {
    case 'confirmed':
      return 'Confirmed by you';
    case 'expiring':
      return 'Due for renewal';
    case 'lapsed':
      return 'Renewal overdue';
    case 'restate_needed':
      return 'Wording changed — please restate';
    default:
      return 'Needs your confirmation';
  }
}

export function confirmationStatusDetail(c: {
  status: string;
  days_until_expiry?: number;
  expires_on?: string;
  signed_by?: string;
  signed_at?: string;
  signed_on?: string;
  renews_every_months: number;
}): string {
  // `signed_on` is a DATE from the server's own clock. `signed_at` is a
  // timestamp whose first ten characters can be the previous day.
  const on = c.signed_on ?? (c.signed_at ? c.signed_at.slice(0, 10) : '');
  switch (c.status) {
    case 'confirmed':
      return `${c.signed_by} confirmed this on ${on}. It stands until ${c.expires_on}.`;
    case 'expiring':
      return `${c.signed_by} confirmed this on ${on}. It runs out in ${c.days_until_expiry} days, on ${c.expires_on}.`;
    case 'lapsed':
      return `${c.signed_by} confirmed this on ${on}, and it ran out on ${c.expires_on}. Confirm it again to keep it current.`;
    case 'restate_needed':
      return `${c.signed_by} confirmed this on ${on}, but the requirement has since been reworded. Your earlier statement stands as a record of what you said that day; it does not answer the sentence below.`;
    default:
      return `Nobody at your practice has confirmed this yet. Once confirmed it stands for ${c.renews_every_months} months.`;
  }
}

/** Said at the top of the tab, and again on every sign sheet. */
export const CONFIRMATION_INTRO =
  'Some requirements depend on how your practice works, such as whether an exam happens before prescribing. Only someone at the practice can confirm them. Your signed statement goes to counsel for review.';

/** The line that stops a confirmation from reading as a resolution. */
export const CONFIRMATION_EFFECT =
  'Your signed statement is saved with your practice details for your counsel to review.';

/** The intent sentence. This IS the signature, so it says what is being agreed. */
export const CONFIRMATION_INTENT =
  'I confirm the statement above is true of my practice, and I am authorised to say so on its behalf. I understand this is recorded as my electronic signature.';

/** Consent to transact electronically, presented WITH the record being signed. */
export const CONFIRMATION_CONSENT =
  'I agree to the terms above and to signing electronically. I can request a paper copy of anything I sign here at any time.';

/** The reach of an answer, stated so it cannot be read as more than it is.
 *
 *  The first version of this screen said "Covers 478 results" for Advanced
 *  Vitality's examination requirement, because the rule applies to all 478.
 *  Confirming it would have moved 49: the other 429 are held shut by an
 *  unlicensed prescriber and no statement about examinations touches them.
 *  A practice that signs expecting 478 and gets 49 has been misled by us, so
 *  the two numbers are now said separately and in that order. */
export function confirmationReach(
  situations: number,
  alsoApplies: number,
  signed = false,
): string {
  const n = Number(situations) || 0;
  // Tense follows the state. A card headed "Confirmed by you" that still reads
  // "Confirming this resolves 49 results" asks for something already done.
  // "Resolves" can imply a guaranteed verdict. Confirmation supplies a fact;
  // the deterministic assessment decides the resulting status.
  const head = signed
    ? `Your statement applies to ${n.toLocaleString()} ${n === 1 ? 'result' : 'results'}`
    : `Your signature records a statement for ${n.toLocaleString()} ${
        n === 1 ? 'result' : 'results'
      }`;
  if (!Number.isFinite(alsoApplies) || alsoApplies <= 0) return head + '.';
  return (
    head +
    `. It also applies to ${alsoApplies.toLocaleString()} other results. Those stay ` +
    'open because another requirement is unresolved, often a missing licence.'
  );
}

/** The explanation, minus the sentence already shown above it verbatim. */
export function confirmationDetail(
  statement: string,
  explanation: string | null,
): string | null {
  if (!explanation) return null;
  const rest = explanation
    .trim()
    .startsWith(statement.trim().replace(/\.$/, ''))
    ? explanation.trim().slice(statement.trim().replace(/\.$/, '').length)
    : explanation.trim();
  const cleaned = rest.replace(/^[.\s]+/, '').trim();
  return cleaned.length > 0 ? cleaned : null;
}

/** When the snapshot is too old to say what needs confirming.
 *
 *  An empty list rendered as "nothing needs a confirmation from you" — which
 *  on Advanced Vitality's own data would have reported 49 items of the
 *  practice's own work as none. "We cannot tell from this version" and
 *  "there is nothing" are opposite claims. */
export const CONFIRMATION_NOT_DERIVABLE =
  'We cannot tell which statements apply from the information on file. Review your practice details or contact your VerityRx team.';

export const CONFIRMATION_NONE =
  'Nothing to sign right now.';

/** Before the first report exists: the statements come from it. A state, not a failure. */
export const CONFIRMATION_AWAITING_REPORT =
  'The statements your practice signs will appear here with your first report.';

/** When there is nothing left to do BECAUSE it was done. "Nothing needs a
 *  confirmation" beside a card reading "Confirmed by you" reads as though the
 *  work was never needed rather than as though it is finished. */
export function confirmationAllDone(n: number, results: number): string {
  return (
    `You are up to date. ${n} ${n === 1 ? 'requirement is' : 'requirements are'} ` +
    `confirmed, covering ${results.toLocaleString()} of your results. ` +
    'We will ask you to confirm again before each one runs out.'
  );
}

/** Shown when a screen was served from a different analysis than the masthead.
 *  Not a silent refresh: the numbers already on screen came from the older one,
 *  and replacing them under the reader is how someone quotes a figure that is
 *  no longer there. */
export const VERSION_MOVED =
  'A new assessment was published while this page was open. The figures may differ. Reload to see the new version.';

/** The ask that is real even when the cell count is zero.
 *
 *  Advanced Vitality's screen reads "More information needed: 0" while three
 *  named files are outstanding. Both are true of different things — the count
 *  is assessed cells blocked on a document; the requests are about activity we
 *  could not assess at all — but a practice owner reads the two side by side
 *  and concludes we contradict ourselves, or that they owe us nothing. */
export function dataRequestNote(open: number): string | null {
  if (open <= 0) return null;
  return `We also need ${open} ${
    open === 1 ? 'file' : 'files'
  } to check activity outside these results.`;
}

/** Shown on the "waiting on your confirmation" card once they have signed.
 *
 *  The screen used to keep saying "49 waiting on you" after all 49 were
 *  signed. The cells really are still unresolved — they move when the next
 *  binder is built — but "you have not done this" and "what you did has not
 *  taken effect yet" are different sentences, and only the second is true. */
export function alreadySignedNote(signed: number, total: number): string | null {
  if (signed <= 0) return null;
  if (signed >= total) {
    return 'You have confirmed all of these. Your signed statements are saved with your practice details for counsel review.';
  }
  return `You have confirmed ${signed.toLocaleString()} of these. Your signed statements are saved with your practice details for counsel review.`;
}

/** What signing is, when the requirement talks about documents.
 *
 *  AV's examination requirement says a documented workflow must be "on file"
 *  or the result stays unresolved — and the only control on the card is a
 *  signature. A reader is told they need a document and offered a checkbox,
 *  with nothing saying whether the checkbox is a substitute, a promise, or a
 *  misunderstanding. It is none of those: the signature is the record WE keep,
 *  and the document, if they have one, is what their counsel will want. */
export const CONFIRMATION_DOCUMENT_NOTE =
  'This requirement mentions documentation. Your signature records your answer here; it does not replace your practice’s records. If you have a written workflow or policy, send it under “Send us files” so counsel can review it and we can check it.';

/** How a cause's unresolved situations split by who can close them.
 *
 *  The examination requirement reads 60 on the causes tab and 49 on the
 *  confirmations tab. Both are right — the other 11 are grey for a reason the
 *  practice cannot act on — but a reader who notices the gap and cannot
 *  reconcile it concludes we cannot count, which costs more than either
 *  number. */
export function unresolvedSplit(
  by: Partial<Record<GreyKind, number>> | undefined,
): string | null {
  if (!by) return null;
  const parts: string[] = [];
  const add = (k: GreyKind, phrase: string) => {
    const n = by[k] ?? 0;
    if (n > 0) parts.push(`${n.toLocaleString()} ${phrase}`);
  };
  add('awaiting_confirmation', 'waiting on your confirmation');
  add('input_missing', 'waiting on a document');
  add('counsel_decides', 'for your counsel to decide');
  add('not_yet_reviewed_by_us', 'not yet reviewed by us');
  if (parts.length === 0) return null;
  return `Of these, ${parts.join(', ')}.`;
}

/** Withdrawing a statement that has stopped being true.
 *
 *  Without this the only way out of a signed confirmation is to keep it: a
 *  practice that changed how it operates stays attached to a statement that
 *  has become false, and the next build honours it. That is a false compliant
 *  carrying the customer's own signature. */
export const WITHDRAW_PROMPT =
  'If this is no longer true of your practice, say so. Your signed statement stays on the record as what you said at the time — withdrawing it means we stop using it, and these results go back to waiting on you.';
export const WITHDRAW_REASON_LABEL = 'What changed?';

/**
 * The words for a provider type on a customer surface.
 *
 * `NP_APRN` / `MD_DO` / `PA` are the fee model's internal keys. They are ours,
 * not the practice's, and a practice owner reading "NP_APRN1 prescriber" on
 * their own cost page is reading our workbench. The display-strings gate does
 * not catch these — its token list is the verdict vocabulary — so the rule is
 * kept here, in the one module allowed to hold customer wording.
 */
export function providerTypeLabel(key: string): string {
  switch (key) {
    case 'MD_DO':
      return 'Physicians';
    case 'NP_APRN':
      return 'Nurse practitioners';
    case 'PA':
      return 'Physician assistants';
    default:
      return 'Other clinician';
  }
}
export const WHAT_IF_NOTE = 'this comparison does not change any report or finding.';

// --------------------------------------------------------------------------- //
// Direct-entry assessment status/current-result (BL-651, E6). A different
// pipeline from the binder-reviewed screens above: no lawyer has looked at
// this, and every string here says so rather than letting the two surfaces
// read as the same claim.
// --------------------------------------------------------------------------- //

/** Shown in place of the "Request an assessment" button whenever the server
 * reports the worker is not configured (E5 unshipped). Never a countdown or
 * an ETA this code has no basis for. */
export const WORKER_NOT_CONFIGURED =
  'Your answers are saved. Automatic checks are currently off for your practice. Contact us to request a check.';

export type AssessmentWorkState = 'queued' | 'settling' | 'running' | 'off';
export function assessmentWorkState(status: {
  worker_available: boolean;
  latest_request: { status: string; is_terminal: boolean; ready_at?: string | null } | null;
} | null): AssessmentWorkState | null {
  if (!status) return null;
  if (!status.worker_available) return 'off';
  const latest = status.latest_request;
  if (latest && !latest.is_terminal) {
    if (latest.status === 'queued' && latest.ready_at && Date.parse(latest.ready_at) > Date.now()) return 'settling';
    return latest.status === 'queued' ? 'queued' : 'running';
  }
  return 'queued';
}
export const ASSESSMENT_WORK_COPY: Record<AssessmentWorkState, string> = {
  queued: 'We will check your saved changes automatically.',
  settling: 'We are grouping your recent changes for one check.',
  running: 'Checking your practice details…',
  off: WORKER_NOT_CONFIGURED,
};

/** The binder generator is a SEPARATE runtime from the assessment worker
 * (a healthy assessment says nothing about PDF rendering), so its off-state
 * note is a different sentence -- never the assessment sentence twice on one
 * screen. */
export const BINDER_NOT_CONFIGURED =
  'Your assessment results are available here. PDF reports are not turned on for your practice yet. Contact us if you need the PDF sooner.';

/** A minimal least-recently-known server code, in plain English -- never the
 * raw snake_case/colon-joined form the engine uses internally. A code not
 * named here falls back to a fixed sentence rather than the separator-to-
 * space substitution this used to do, which put a raw production error code
 * ("source rows missing") on a customer screen the moment a new one shipped
 * server-side (2026-09-22 review item 5). */
export function humanizeReason(code: string): string {
  switch (code) {
    case 'compute_error':
      return 'an internal error';
    case 'max_attempts_exceeded':
      return 'repeated attempts did not complete';
    case 'superseded_during_retry':
      return 'a newer request replaced it before this one could retry';
    case 'no_compatible_grid_cell':
      return 'nothing in our records matches this combination';
    default:
      return 'something on our side did not complete';
  }
}

/** A resolved source, safe to link: a real URL, shown by its host + path
 *  rather than the internal citation key. */
export interface ResolvedCitation {
  url: string;
  label: string;
}

function parseHttpUrl(value: string): URL | null {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u : null;
  } catch {
    return null;
  }
}

/** A finding's raw `citations` field (`FindingRecord.citations`,
 *  `app/services/telehealth_adapter.py`'s `citations = sorted({c for tr in
 *  ... for c in tr.get("citations", [])})`) mixes real source URLs with bare
 *  register citation KEYS the rule engine uses internally before a source is
 *  resolved ("dea_2006_final_rule", "oh_med", "per_state:np_authority (...
 *  STATE_NPPA_SOURCE)") -- the live-assessment wire has no lookup turning the
 *  latter into a resolved `Citation` the way `customer/api.ts`'s binder-backed
 *  surfaces get. Printing a key raw was the defect (2026-09-21 production
 *  review, polish item 2): this renders ONLY entries that parse as an actual
 *  http(s) URL, deduplicated by URL; everything else is counted, never
 *  named, as `unresolvedCount`. */
export function resolveCitations(raw: (string | null | undefined)[]): {
  resolved: ResolvedCitation[];
  unresolvedCount: number;
} {
  const seen = new Set<string>();
  const resolved: ResolvedCitation[] = [];
  let unresolvedCount = 0;
  for (const c of raw) {
    if (!c) continue;
    const url = parseHttpUrl(c.trim());
    if (!url) {
      unresolvedCount += 1;
      continue;
    }
    const key = url.toString();
    if (seen.has(key)) continue;
    seen.add(key);
    const path = url.pathname === '/' ? '' : url.pathname;
    resolved.push({ url: key, label: `${url.host}${path}` });
  }
  return { resolved, unresolvedCount };
}

/** The unresolved-citation summary line -- a count, never a key, per
 *  `resolveCitations`'s own contract. */
export function unresolvedSourcesNote(count: number): string {
  return `${count.toLocaleString()} source${count === 1 ? '' : 's'} pending verification`;
}

/** Why the current result is not necessarily what a newer request would
 * show -- distinguishes a newer request already in flight, one that failed,
 * and a plain workspace edit, rather than one generic "may be out of date"
 * line for every cause. */
export function staleBanner(
  latest: { status: JobStatusForBanner; is_terminal: boolean } | null,
): string {
  if (latest && !latest.is_terminal) {
    return 'A newer request is in progress. This is your last completed result until it finishes.';
  }
  if (latest && latest.status === 'failed') {
    return "A newer request did not complete. This is your last completed result — it hasn't changed.";
  }
  return 'Your practice details have changed since this result was computed. Check this setup again for a current answer.';
}

type JobStatusForBanner =
  | 'queued'
  | 'leased'
  | 'running'
  | 'completed'
  | 'failed'
  | 'superseded';

// --------------------------------------------------------------------------- //
// Counsel binder + review status (BL-648, package 6E/6F).
//
// The wire value (`none`/`requested`/`failed`/`generated`/`in_review`/
// `changes_requested`/`signed_off`) is derived server-side from real
// records — never rendered raw, and never worded to imply a step happened
// that the database does not actually show (see
// dashboard/services/telehealth_binder.py's module docstring for exactly
// what each one means). This is also the ONE place "sent for review" /
// "signed off" wording lives, so the promotion screen's own internal name
// ("operator", "the review workspace") never needs to leak here — a
// customer is told the truthful STATE, never how it got there.
// --------------------------------------------------------------------------- //

export type BinderReviewStatusForLabel =
  | 'none'
  | 'requested'
  | 'failed'
  | 'generated'
  | 'in_review'
  | 'changes_requested'
  | 'signed_off';

/** ONE vocabulary for a report's review standing (BL redesign, 2026-09-21,
 *  W3 package 8 "Your reports") -- these five words, plus `Current` /
 *  `Previous` (position within the versions list, `reportPositionWord`
 *  below), are the ONLY words this product uses for the concept anywhere on
 *  the customer surface. Replaces the drift between this function and
 *  `reviewBanner` (the same six real states worded independently, found in
 *  the 2026-09-21 redesign review) for every caller this package owns;
 *  `reviewBanner` itself stays only inside the legacy, now-unrouted
 *  `CustomerShell.tsx` (not this package's file to edit). */
export function binderReviewStatusLabel(
  status: BinderReviewStatusForLabel,
  unreviewed?: number | null,
  reviewSince?: string | null,
): string {
  switch (status) {
    case 'none':
      return 'No PDF requested yet';
    case 'requested':
      return 'Building your PDF…';
    case 'failed':
      return 'This attempt did not complete';
    case 'generated':
      return 'PDF ready — not yet sent to counsel';
    case 'in_review':
      // TODO(Shawn, PM-38): "your lawyer" means two different people on two pages ("your reviewing lawyer"
      // here, "take this to your lawyer" on Where you stand). The proposed words ("a lawyer working with
      // VerityRx is reviewing this report" / "you can also ask your own lawyer") change what the status
      // MEANS, so they wait for his decision; nothing here has been reworded.
      return reviewSince
        ? `With your reviewing lawyer for review since ${reviewSince}`
        : 'A lawyer should confirm this report before you rely on it.';
    case 'changes_requested':
      return 'Changes requested';
    case 'signed_off':
      return unreviewed ? `Signed off with ${unreviewed} ${unreviewed === 1 ? 'finding' : 'findings'} undecided` : 'Signed off';
    default:
      return status;
  }
}

/** The versions list's position badge -- orthogonal to review standing
 *  above (a version can be Current while a counsel round is open), so it is
 *  a separate word, never folded into the same switch. */
export function reportPositionWord(isCurrent: boolean): 'Current' | 'Previous' {
  return isCurrent ? 'Current' : 'Previous';
}

/** Plain-word refusal reason for "Request the counsel PDF for this
 *  version" -- keyed by the reason this screen itself derived (there is
 *  currently no assessed finding to report on), never a raw server code. */
export function reportRequestRefusalReason(reason: 'no_assessed_findings'): string {
  switch (reason) {
    case 'no_assessed_findings':
      return 'This version has no assessed results yet, so there is nothing to put in a report.';
    default:
      return 'This report cannot be requested right now.';
  }
}

/** One sentence of what that state actually means, for the line under the
 * badge -- never implies a step (sent, reviewed, signed) that has not
 * actually happened in the database. */
export function binderReviewStatusDetail(
  status: BinderReviewStatusForLabel,
  unreviewed?: number | null,
  /** The server's sentence naming who recorded the outcome ("Signed off by
   *  jane@firm (counsel)." / "Signed off. Recorded by ops@… (VerityRx team)
   *  on counsel's behalf: …"). Arvind 2026-09-29: never "Your counsel …"
   *  unless counsel did it; a round recorded before this was captured reads
   *  neutrally. */
  completedBy?: string | null,
): string {
  const notReviewed = unreviewed
    ? ` ${unreviewed} ${unreviewed === 1 ? 'finding was' : 'findings were'} not reviewed.`
    : '';
  if (completedBy && (status === 'signed_off' || status === 'changes_requested')) {
    return `${completedBy}${status === 'signed_off' ? notReviewed : ''} This is not, by itself, the signed deliverable document.`;
  }
  switch (status) {
    case 'none':
      return 'Request a PDF frozen to your current result.';
    case 'requested':
      return 'This can take a few minutes. This page updates on its own.';
    case 'failed':
      return 'Your previous valid PDF, if any, is still available below. You can retry.';
    case 'generated':
      return 'This PDF has not been sent for counsel review yet.';
    case 'in_review':
      return 'This version is open for your reviewing lawyer.';
    case 'changes_requested':
      return 'The review round closed with a request for changes. This is not the signed deliverable.';
    case 'signed_off':
      return `The review round was signed off.${notReviewed} This is not, by itself, the signed deliverable document.`;
    default:
      return '';
  }
}

/** Shown next to a PREVIOUS version that is no longer current -- never
 * silently dropped just because a newer attempt exists or failed. */
export const STALE_ARTIFACT_LABEL = 'Previous version — not your current result';

// --------------------------------------------------------------------------- //
// Shell redesign (2026-09-21, W1): the rail's group/section labels, the
// landing card copy, and the plain-word mapping for why a cell could not be
// checked. Nothing here duplicates a sentence already owned above -- the
// verdict words, the confirmation vocabulary and the binder-review words stay
// exactly where they are; this section is additive.
// --------------------------------------------------------------------------- //

/** Words that must never reach a customer screen (brief 2026-09-21). The
 *  copy gate (W3's `check-copy-words` extension) scans rendered text for
 *  these; kept here, beside the vocabulary it is checking, rather than in
 *  the gate script itself, so the banned list and the words actually used
 *  on this surface can never drift apart without one file changing. */
export const BANNED_CUSTOMER_WORDS = [
  'snapshot',
  'sha',
  'job',
  'lease',
  'worker',
  'operator',
  'internal',
  'promote',
  'export',
  'vertical',
  'entitlement',
  'engine',
] as const;

/** The rail's four groups, in order. Section keys live in
 *  `customer/shell/sections.ts` (the registry); this is only the group
 *  label each key renders under. */
export const RAIL_GROUP_LABEL = {
  setup: 'Set up your practice',
  results: 'Your results',
  law: 'The law',
  current: 'Keeping current',
  account: 'Account',
} as const;

export const SHELL_SECTION_LABEL = {
  practice: 'Your practice',
  prescribers: 'Prescribers',
  prescribing: 'Prescribing',
  agreements: 'Agreements',
  need: 'Still needed',
  stand: 'Where you stand',
  cases: 'Corrective actions',
  campaigns: 'Record campaigns',
  notchecked: 'What we could not check',
  reports: 'Your reports',
  changes: 'What changed in your results',
  // While results are held only the practice's own edits are shown, so the rail and the page title both say so (COHERE4 P2-7).
  changesHeld: 'What you changed',
  law: 'Changes in the law',
  calendar: 'Licences and deadlines',
  renewals: 'Licences and deadlines',
  files: 'Send us files',
  access: 'People with access',
  summary: 'Your summary',
  package: 'Audit package',
} as const;

export const PAGE_SHOW = 'Show';
export const PAGE_MORE = 'more';
export const PAGE_SHOW_FEWER = 'Show fewer';
export const PAGE_NOT_ANSWERED = 'Not answered';
export const PAGE_NOT_ANSWERED_AFFECTS = (n: number) => `Not answered · affects ${n.toLocaleString()} checks`;

/** The welcome card: no practice profile yet. Three sentences, as the brief
 *  asks -- never a bare heading with nothing under it. */
export const LANDING_WELCOME_LEAD =
  'Add your practice, patient states, and prescribers to start a check. You can save and come back.';
export const LANDING_WELCOME_PRIMARY = 'Set up your practice';
export const LANDING_WELCOME_SECONDARY = 'Import a file';

/** Setup started, no completed check yet: the one-line strip and the CTA
 *  that requests a live check once readiness allows a partial one. */
export function landingProgressNote(done: number, remaining: number): string {
  const total = done + remaining;
  return (
    `${done} of ${total} setup ${total === 1 ? 'step is' : 'steps are'} done` +
    (remaining > 0 ? `, ${remaining} to go.` : '.')
  );
}
export const LANDING_CHECK_ACTION = 'Run a check';
export const LANDING_CHECK_AGAIN_ACTION = 'Check again';
export const LANDING_CHECK_UNAVAILABLE =
  'Add a prescriber, patient state, and medication to run your first check.';
/** The truthful version of `LANDING_CHECK_UNAVAILABLE` for a practice that
 *  has already run checks -- "run your first check" is false of a practice
 *  with hundreds on file, even while a step above still blocks a NEW one
 *  (a state or prescriber whose current setup regressed after the last
 *  check ran). Never claims a check number or date we do not have. */
export function landingChecksAlreadyRun(checksRun: number, lastCheckedAt: string | null): string {
  const count = `Checks run: ${checksRun}`;
  const since = lastCheckedAt ? ` · last re-check ${formatViewerDate(lastCheckedAt)}` : '';
  return `${count}${since}. Finish the setup steps above, then check again.`;
}

/** Completed-assessment banner, when the facts moved since it ran. Replaces
 *  the retired binder-only `VERSION_MOVED` wording for THIS surface -- a
 *  live result is refreshed by requesting a new one, not by reloading a
 *  page under a reader who is mid-read. */
export const RESULT_STALE_BANNER =
  'Your result was updated — refresh to see the current one, or request a new check from Setup.';

/** "Where you stand" header line. */
export const STAND_COMPUTED_NOTE = 'Based on your saved details. Counsel has not reviewed this assessment.';

/** Date-shifted checks from the same pinned assessment, never a new verdict. */
export const upcomingHeading = (date: string) => {
  const parsed = new Date(`${date}T12:00:00Z`);
  return `Coming ${Number.isNaN(parsed.getTime()) ? date : new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(parsed)}`;
};
export const upcomingIntro = (n: number) =>
  `Using your current details, ${n} prescribing ${n === 1 ? 'check has' : 'checks have'} a rule that changes on this date. This is a preview, not a new assessment.`;
export const upcomingOpen = (n: number) => `See ${n} affected ${n === 1 ? 'check' : 'checks'}`;
export const UPCOMING_CLOSE = 'Hide affected checks';
export const UPCOMING_RULES = 'Rules changing';
export const UPCOMING_SOURCE = 'Official source';
export const UPCOMING_SOURCE_MISSING = 'Source is not pinned for this preview';
export const UPCOMING_QUESTION = 'What to confirm';
export const upcomingRuleMovement = (from: EngineVerdict | null, to: EngineVerdict | null) => {
  const label = (v: EngineVerdict | null) => v === 'compliant' ? 'Met' : v === 'non_compliant' ? 'Not met' : v === 'grey' ? 'Needs review' : 'Did not apply';
  return `Rule: ${label(from)} now → ${label(to)} on this date`;
};
export const upcomingMovement = (from: EngineVerdict | null, to: EngineVerdict | null) => {
  const label = (v: EngineVerdict | null) => v === 'compliant' ? 'Meets requirements' : v === 'non_compliant' ? 'Requirement not met' : v === 'grey' ? 'Needs review' : 'Not assessed';
  return from === to
    ? `The check still ${to === 'grey' ? 'needs review' : to === 'compliant' ? 'meets requirements' : to === 'non_compliant' ? 'does not meet a requirement' : 'has not been assessed'}, but a rule changes.`
    : `${label(from)} now → ${label(to)} on this date, using your current details.`;
};

/** The stated denominator: how much of what was declared actually has a
 *  RESULT (a non-null verdict) -- distinct from `total` (everything
 *  declared) AND from how the resolved part came out (ok/no/grey, which the
 *  composition bar already shows). The earlier version compared `total` to
 *  the count of `record_type: 'finding'` rows only, so a practice whose
 *  every declared cell was ATTEMPTED but whose evidence never resolved any
 *  of them (a bulk import outrunning credential review) read "All N
 *  declared situations have been checked." -- true of the attempt, false of
 *  the answer (2026-09-21 production review, polish item 3: 12 declared, 0
 *  with a result, all 12 read as "checked"). States the split explicitly
 *  instead so a clean-looking bar can never stand in for an unresolved
 *  practice: `withResult` is findings with a non-null verdict; the rest of
 *  `total` -- verdict-null findings AND scope-incompatible rows alike -- is
 *  `notAssessable`. */
export function resultSplitNote(total: number, withResult: number, notAssessable: number): string {
  if (total === 0) return 'Nothing has been declared under Setup to check yet.';
  return (
    `${total.toLocaleString()} ${total === 1 ? 'check' : 'checks'} run · ` +
    `${withResult.toLocaleString()} with a result · ` +
    `${notAssessable.toLocaleString()} not yet possible`
  );
}

/** One `kind:state` pair parsed out of the wire's comma-joined
 *  `unassessed_reason` field. */
interface UnassessedPair {
  kind: string;
  state: string;
}

const UNASSESSED_KIND_LABEL: Record<string, string> = {
  training: 'training records',
  ky_led_fda_prescription_device: 'whether this LED device is an FDA prescription medical device',
  state_license: 'state licence',
  dea_registration: 'DEA registration',
  state_csr: 'state controlled-substance registration',
  prescriptive_authority: 'state prescribing authorization',
  ms_cspa: 'Mississippi controlled-substance prescribing approval',
  collaboration_agreement: 'collaboration agreement',
  pa_crnp_agreement: 'Pennsylvania CRNP prescriptive collaborative agreement checklist',
  ky_capa_ns: 'Kentucky agreement for non-controlled medications (CAPA-NS)',
  ky_capa_cs: 'Kentucky agreement for controlled substances (CAPA-CS)',
  ky_capa_ns_exemption: 'Kentucky exemption from the CAPA-NS',
  ky_capa_cs_exemption: 'Kentucky exemption from the CAPA-CS',
  wi_aprn_transition: 'Wisconsin APRN prescribing pathway and licence notation',
  az_pa_ii_iii_approval: 'Arizona PA Schedule II and III Board approval',
  al_schedule_ii_lpsp: 'Alabama Schedule II permit and approved formulary',
  la_aprn_site_cs_scope: 'Louisiana APRN practice-site and controlled-schedule approval',
  md_aprn_role_scope: 'Maryland APRN certified-role prescribing scope',
  nm_non_cnp_formulary: 'New Mexico CNS/CRNA role formulary',
  or_crna_outpatient_scope: 'Oregon CRNA outpatient prescription scope',
  agreement_exemption: 'answer on the state\'s own condition for practising without an agreement',
  supervisee_count: 'count of the nurse practitioners and physician assistants the supervising physician oversees in the state',
  provider_location_dea_registration: 'DEA registration at the prescriber’s work location',
  provider_location_state: 'state where the prescriber is working during these visits',
  compounding_pathway: 'medication formulation and compounding pathway',
  compounding_503b_ingredient_basis: 'ingredient basis for this medication from a 503B outsourcing facility',
  compounding_route_contradiction: 'what is actually dispensed for a medication marked not compounded',
  fl_no_office_or_in_person: 'no Florida office or in-person services under telehealth registration',
  prescribing_care_setting: 'Care setting for these prescriptions',
  prescribed_drug_use_site: 'Where prescribed medication will be used',
  fl_schedule_ii_exception: 'Florida Schedule II telehealth exception',
  out_of_state_telehealth_registration: 'out-of-state telehealth registration',
  fl_registration_exemption: 'Florida emergency or consultation registration exemption',
  encounter_modality: 'visit format',
  tx_relationship_basis: 'clinician–patient relationship for remote visits',
  tx_new_relationship_followup: 'follow-up for new remote relationships',
  tx_chronic_pain: 'chronic-pain prescribing',
  tx_established_pain_patient: 'established pain-patient status',
  tx_identical_repeat: 'identical repeat prescriptions',
  tx_qualifying_visit_90d: 'qualifying visits within 90 days',
  tx_exception_documented: 'recorded reason for a prescribing exception',
  tx_factors_considered: 'factors considered for a prescribing exception',
};

const UNASSESSED_STATE_PHRASE: Record<string, string> = {
  not_recorded: 'not recorded',
  undecided: 'not yet decided',
  board_verification_missing: 'pending verification against the board register',
  withdrawn: 'withdrawn, with nothing on file to replace it',
  contradictory: 'contradictory and needs review',
};

/** The NP supervision/collaboration instrument, by state -- state-correct
 *  words for the ONE `collaboration_agreement` kind (2026-09-22 review,
 *  LR-REPORT item G-15): Ohio's is a "standard care arrangement" (ORC
 *  4723.431), Kentucky's a "CAPA-CS" (KRS 314.042) -- "collaboration
 *  agreement" is the engine's own kind name, not either state's, and a
 *  Kentucky-licensed NP reading it would not recognise the request.
 *  Additive alongside `UNASSESSED_KIND_LABEL` (never edited: that table's
 *  shape is what `test_unassessed_words.py` parses to hold the Python
 *  mirror `app/services/binder/unassessed_words.py` in step) -- this is
 *  consulted ONLY for `collaboration_agreement` and ONLY when a state code
 *  is known; every other kind, and an unknown state, falls back to the
 *  generic word untouched. */
const NP_INSTRUMENT_BY_STATE: Record<string, string> = {
  OH: 'Ohio standard care arrangement',
  KY: 'CAPA-CS',
};

/** AGREEFIX2 (AGREETXT wording findings, 2026-09-29): where a state's law for
 *  ONE role is not a signed agreement at all, the words name what that law
 *  asks for -- the credential kind stays `collaboration_agreement`. Keyed
 *  `STATE:ROLE`; consulted before `NP_INSTRUMENT_BY_STATE`. Mirrors
 *  `STATE_ROLE_KIND_WORDS` in apps/api app/services/binder/unassessed_words.py
 *  (held in step by test_unassessed_words.py). */
const INSTRUMENT_BY_STATE_ROLE: Record<string, string> = {
  'FL:PA': 'supervising physician\'s prescribing-delegation notice',
  'NY:PA': 'supervising physician\'s supervision record',
  'GA:PA': 'Board-approved job description',
  'WV:PA': 'activated practice notification',
  'CO:NP': 'prescribing mentorship with a physician or a full-authority APRN',
};

/** The role's own instrument in a state whose law for that role is not a
 *  signed agreement, or null where the neutral words stand. */
export function instrumentForStateRole(stateCode: string | null | undefined, role: string | null | undefined): string | null {
  if (!stateCode || !role) return null;
  return INSTRUMENT_BY_STATE_ROLE[`${stateCode}:${role.toUpperCase()}`] ?? null;
}

/** One sentence per state of an agreement question whose law for this role
 *  asks for something other than a signed agreement. */
export function agreementInstrumentNotes(role: string | null | undefined, stateCodes: string[], stateLabel: (code: string) => string): string[] {
  return stateCodes.flatMap((code) => {
    const instrument = instrumentForStateRole(code, role);
    return instrument ? [`In ${stateLabel(code)} this is the ${instrument}.`] : [];
  });
}

function capitalizeFirst(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}

function unassessedKindLabel(kind: string, stateCode?: string | null, role?: string | null): string {
  if (kind === 'collaboration_agreement') {
    const own = instrumentForStateRole(stateCode, role);
    if (own) return own;
  }
  if (kind === 'collaboration_agreement' && stateCode) {
    const instrument = NP_INSTRUMENT_BY_STATE[stateCode];
    if (instrument) return instrument;
  }
  return UNASSESSED_KIND_LABEL[kind] ?? kind.replace(/_/g, ' ');
}

function unassessedStatePhrase(state: string): string {
  return state ? (UNASSESSED_STATE_PHRASE[state] ?? state.replace(/_/g, ' ')) : 'needs review';
}

function parseUnassessedPairs(reason: string): UnassessedPair[] {
  return reason
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const idx = p.indexOf(':');
      return idx === -1
        ? { kind: p, state: '' }
        : { kind: p.slice(0, idx), state: p.slice(idx + 1) };
    });
}

/** One lead sentence for "why can't this be checked", one destination
 *  action, and every pair behind it for a "Details" disclosure. Replaces a
 *  raw `humanizeReason(f.unassessed_reason)` call, which read the
 *  comma-joined `kind:state` wire value (`app/services/telehealth_adapter.py`'s
 *  `reason_parts`) as separator-to-space substitution ONLY -- commas
 *  untouched -- and rendered exactly the regression this fixes: "collaboration
 *  agreement not recorded,dea registration not recorded,state csr not
 *  recorded,state license board verification missing" (2026-09-21
 *  production review, polish item 1). A row can carry several parts; this
 *  picks ONE lead by a fixed priority (a board check in progress outranks
 *  everything else a practice cannot act on; a missing licence/DEA/
 *  agreement it CAN act on comes next; a missing state CSR alone is its own
 *  reason only when nothing else explains the row) rather than concatenating
 *  every part into one run-on sentence. */
export function describeUnassessed(
  reason: string | null | undefined,
  /** The cell's own patient state, when known -- used ONLY to pick the
   *  state-correct NP instrument name for `collaboration_agreement`
   *  (LR-REPORT item G-15). Every other reason is unaffected by it. */
  stateCode?: string | null,
  /** The prescribing capacity (licence type), when known -- picks the role's
   *  own instrument where the state's law for that role is not a signed
   *  agreement (AGREEFIX2). */
  role?: string | null,
): {
  lead: string;
  details: string[];
  action: { label: string; section: SectionKey } | null;
} {
  const fallback = { lead: 'This has not been reviewed yet.', details: [] as string[], action: null };
  if (!reason) return fallback;
  const pairs = parseUnassessedPairs(reason);
  if (pairs.length === 0) return fallback;
  if (pairs.some((p) => p.kind === 'historical_rule_history' && p.state === 'unknown')) {
    return {
      lead: 'We cannot confirm every requirement that applied on this earlier date because earlier rule versions and status dates are not retained.',
      details: [
        'This result uses the rule and reference text pinned when the assessment was requested; it does not reconstruct earlier versions or earlier practice answers.',
      ],
      action: null,
    };
  }

  const details = pairs.map(
    ({ kind, state }) =>
      `${capitalizeFirst(unassessedKindLabel(kind, stateCode, role))}: ${unassessedStatePhrase(state)}.`,
  );
  const has = (kind: string, state: string) => pairs.some((p) => p.kind === kind && p.state === state);

  const boardPending = pairs.find((p) => p.state === 'board_verification_missing');
  const undecided = pairs.find((p) => p.state === 'undecided');
  // G-15's second half: a practice with an agreement in ONE state and none
  // in another reads two differently-worded sentences about "the same
  // document" and cannot tell whether it is one half-entered record or two
  // states that genuinely need two different instruments (LR-REPORT: "same
  // prescriber, two different-sounding statements... If the two states need
  // two different instruments, say so"). They always do -- an agreement is
  // filed per state -- so this one sentence says that whenever the reason
  // involves this kind at all, regardless of which branch below fires.
  const collabNote = pairs.some((p) => p.kind === 'collaboration_agreement')
    ? ` This is specific to this state — an instrument on file for another state does not cover it.`
    : '';

  let lead: string;
  let action: { label: string; section: SectionKey } | null;
  if (boardPending) {
    lead = 'We are verifying this licence against the board register.';
    action = null;
  } else if (undecided) {
    lead = `A supporting document is needed for the ${unassessedKindLabel(undecided.kind, stateCode, role)}.${undecided.kind === 'collaboration_agreement' ? collabNote : ''}`;
    action = { label: 'Send us files', section: 'files' };
  } else if (has('state_license', 'not_recorded')) {
    lead = 'No licence is on file for this state.';
    action = { label: 'Review your prescribers', section: 'prescribers' };
  } else if (has('dea_registration', 'not_recorded')) {
    lead = 'No DEA registration is on file for this state.';
    action = { label: 'Review your prescribers', section: 'prescribers' };
  } else if (has('collaboration_agreement', 'not_recorded')) {
    lead = `No ${unassessedKindLabel('collaboration_agreement', stateCode, role)} is on file for this prescriber.${collabNote}`;
    action = { label: 'Review your agreements', section: 'agreements' };
  } else if (pairs.length === 1 && has('state_csr', 'not_recorded')) {
    lead = 'No state controlled-substance registration is on file.';
    action = { label: 'Review your prescribers', section: 'prescribers' };
  } else {
    const first = pairs[0];
    lead = `${capitalizeFirst(unassessedKindLabel(first.kind, stateCode, role))} is ${unassessedStatePhrase(first.state)}.${first.kind === 'collaboration_agreement' ? collabNote : ''}`;
    action = { label: 'Review your prescribers', section: 'prescribers' };
  }
  return { lead, details, action };
}

/** Plain-word mapping for an unassessed cell's reason code
 * (`kind:evidence_state`, comma-joined by the adapter -- see
 * `app/services/telehealth_adapter.py`'s `reason_parts`). Never rendered as
 * the raw code. Each entry names the ACTION that moves it, in the same
 * three shapes the brief calls out: add a credential, send a document, or
 * an informational note that nothing is owed. */
export interface UnassessedReasonInfo {
  sentence: string;
  action: string;
  /** Which rail section the action button should jump to, when there is
   *  one to jump to. */
  actionSection: SectionKey | null;
}

const CREDENTIAL_KIND_WORD: Record<string, string> = {
  state_license: 'state licence',
  dea_registration: 'DEA registration',
  state_csr: 'state controlled-substance registration',
  prescriptive_authority: 'state prescribing authorization',
  ms_cspa: 'Mississippi controlled-substance prescribing approval',
  collaboration_agreement: 'supervision or collaboration agreement',
  pa_crnp_agreement: 'Pennsylvania CRNP prescriptive collaborative agreement checklist',
  ky_capa_ns: 'Kentucky agreement for non-controlled medications (CAPA-NS)',
  ky_capa_cs: 'Kentucky agreement for controlled substances (CAPA-CS)',
  ky_capa_ns_exemption: 'Kentucky exemption from the CAPA-NS',
  ky_capa_cs_exemption: 'Kentucky exemption from the CAPA-CS',
  wi_aprn_transition: 'Wisconsin APRN prescribing pathway and licence notation',
  az_pa_ii_iii_approval: 'Arizona PA Schedule II and III Board approval',
  al_schedule_ii_lpsp: 'Alabama Schedule II permit and approved formulary',
  la_aprn_site_cs_scope: 'Louisiana APRN practice-site and controlled-schedule approval',
  md_aprn_role_scope: 'Maryland APRN certified-role prescribing scope',
  nm_non_cnp_formulary: 'New Mexico CNS/CRNA role formulary',
  or_crna_outpatient_scope: 'Oregon CRNA outpatient prescription scope',
  agreement_exemption: 'answer on the state\'s own condition for practising without an agreement',
  supervisee_count: 'count of the nurse practitioners and physician assistants the supervising physician oversees in the state',
  provider_location_dea_registration: 'DEA registration at the prescriber’s work location',
  provider_location_state: 'state where the prescriber is working during these visits',
  compounding_pathway: 'medication formulation and compounding pathway',
  compounding_503b_ingredient_basis: 'ingredient basis for this medication from a 503B outsourcing facility',
  compounding_route_contradiction: 'what is actually dispensed for a medication marked not compounded',
  fl_no_office_or_in_person: 'no Florida office or in-person services under telehealth registration',
  prescribing_care_setting: 'Care setting for these prescriptions',
  prescribed_drug_use_site: 'Where prescribed medication will be used',
  fl_schedule_ii_exception: 'Florida Schedule II telehealth exception',
  out_of_state_telehealth_registration: 'out-of-state telehealth registration',
  fl_registration_exemption: 'Florida emergency or consultation registration exemption',
  encounter_modality: 'visit format',
  tx_relationship_basis: 'clinician–patient relationship for remote visits',
  tx_new_relationship_followup: 'follow-up for new remote relationships',
  tx_chronic_pain: 'chronic-pain prescribing',
  tx_established_pain_patient: 'established pain-patient status',
  tx_identical_repeat: 'identical repeat prescriptions',
  tx_qualifying_visit_90d: 'qualifying visits within 90 days',
  tx_exception_documented: 'recorded reason for a prescribing exception',
  tx_factors_considered: 'factors considered for a prescribing exception',
};

function creditKindWord(kind: string): string {
  return CREDENTIAL_KIND_WORD[kind] ?? kind.replace(/_/g, ' ');
}

/** One `kind:evidence_state` part -> its sentence + action. */
function unassessedPartInfo(part: string): UnassessedReasonInfo {
  const [kind, state] = part.split(':');
  const word = creditKindWord(kind ?? '');
  switch (state) {
    case 'not_recorded':
      return {
        sentence: `No ${word} has been declared for this prescriber.`,
        action: 'Add the credential',
        actionSection: 'prescribers',
      };
    case 'undecided':
      return {
        sentence: `A ${word} is on file, but what it currently establishes is not yet decided.`,
        action: 'Review this credential',
        actionSection: 'prescribers',
      };
    case 'withdrawn':
      return {
        sentence: `The ${word} on file was withdrawn, and nothing has replaced it.`,
        action: 'Add a current credential',
        actionSection: 'prescribers',
      };
    case 'board_verification_missing':
      return {
        sentence: `The ${word} is supported by a document you sent, but this requirement needs it confirmed against the issuing board's own record.`,
        action: 'We verify against the board register',
        actionSection: null,
      };
    default:
      return {
        sentence: `The ${word} does not yet establish what this requirement needs.`,
        action: 'Review this credential',
        actionSection: 'prescribers',
      };
  }
}

/** The full comma-joined `unassessed_reason` string -> one entry per part,
 *  de-duplicated by sentence (several cells can share one reason). An
 *  `unresolved_rule_dependency:*` part and anything unrecognized fall back
 *  to an honest "we have not reviewed this yet" rather than a guess. */
export function unassessedReasonInfo(
  reason: string | null | undefined,
): UnassessedReasonInfo[] {
  const fallback: UnassessedReasonInfo[] = [
    {
      sentence: 'We have not reviewed this combination yet.',
      action: 'Nothing for you to do yet',
      actionSection: null,
    },
  ];
  if (!reason) return fallback;
  const parts = reason
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const out: UnassessedReasonInfo[] = [];
  if (parts.includes('historical_rule_history:unknown')) {
    return [{
      sentence: 'We cannot confirm every requirement that applied on this earlier date because earlier rule versions and status dates are not retained.',
      action: 'Nothing for you to do yet',
      actionSection: null,
    }];
  }
  for (const part of parts) {
    const info: UnassessedReasonInfo = part.startsWith('unresolved_rule_dependency:')
      ? {
          sentence: 'This depends on a requirement we have not finished reviewing.',
          action: 'Nothing for you to do yet',
          actionSection: null,
        }
      : unassessedPartInfo(part);
    if (!seen.has(info.sentence)) {
      seen.add(info.sentence);
      out.push(info);
    }
  }
  return out.length > 0 ? out : fallback;
}

/** Scope-incompatibility codes (`UnassessedRow.reason`,
 *  `app/services/telehealth_adapter.py`'s `_compatibility_reasons` /
 *  `scope_grid`) -- a DIFFERENT vocabulary from the credential-evidence
 *  `kind:state` codes above: this cell was never compatible with the grid
 *  at all (the prescriber, the modality, the capacity, or the authority
 *  route did not match), rather than compatible-but-evidence-short. Comma
 *  joined the same way. */
export function scopeReasonInfo(code: string): UnassessedReasonInfo {
  if (code.startsWith('activity_excluded')) {
    return {
      sentence: 'You excluded this row when you reviewed the file.',
      action: 'Nothing for you to do',
      actionSection: null,
    };
  }
  // COHERE5 P0-2: a licence type whose official prescribing-authority source
  // we have not recorded yet. Ours to finish; the binder says the same
  // (`unassessed_words._citation_words`).
  if (code.startsWith('citation_not_recorded')) {
    const type = code.split(':')[1] ?? '';
    const who = (LICENCE_TYPE_WORD[type] ?? 'prescriber').toLowerCase();
    return {
      sentence: `We have not yet recorded the official source for a ${who}\u2019s prescribing authority, so this was not checked.`,
      action: 'Nothing for you to do; we will finish this',
      actionSection: null,
    };
  }
  switch (code) {
    // BL-720: rows the file-drop bridge could not place on the roster.
    // Nothing on our side failed; the roster is missing a person.
    case 'activity_unattributed':
      return {
        sentence: 'The prescriber on this row is not on your roster.',
        action: 'Match these people under Prescribing; no re-import is needed',
        actionSection: 'prescribing',
      };
    case 'activity_ambiguous':
      return {
        sentence: 'More than one prescriber on your roster matches this row.',
        action: 'Review your prescribers',
        actionSection: 'prescribers',
      };
    case 'prescriber_not_in_pinned_snapshot':
    case 'prescriber_not_active':
      return {
        sentence: 'The prescriber on this row is not active on your roster.',
        action: 'Review your roster',
        actionSection: 'prescribers',
      };
    case 'encounter_modality_incompatible':
      return {
        sentence: "This was declared under a modality that does not match your practice's own default.",
        action: 'Review your practice details',
        actionSection: null,
      };
    case 'prescribing_capacity_incompatible':
      return {
        sentence: "This was declared under a capacity that does not match the prescriber's own licence type.",
        action: 'Review this prescriber',
        actionSection: 'prescribers',
      };
    default:
      return {
        sentence: humanizeReason(code).replace(/^./, (c) => c.toUpperCase()) + '.',
        action: 'We have not reviewed this yet',
        actionSection: null,
      };
  }
}

/** The combined mapping for ONE unassessed cell, whichever shape it came
 *  in as: a `record_type: 'unassessed_scope'` row (its own `reason`,
 *  scope-incompatibility codes, comma-joined) or a `record_type: 'finding'`
 *  row whose OWN verdict is null (`unassessed_reason`, credential-evidence
 *  `kind:state` codes, comma-joined). Never both on the same cell in
 *  practice, but reads either field, so `CouldNotCheck` groups the two
 *  shapes together in one plain-word list rather than needing to know
 *  which shape it is looking at. */
export function cellUnassessedInfo(cell: {
  reason?: string | null;
  unassessed_reason?: string | null;
  /** For the state-correct NP instrument name -- see `describeUnassessed`'s
   *  own `stateCode` param (LR-REPORT item G-15). */
  patient_state?: string | null;
  prescribing_capacity?: string | null;
  display_context?: { prescribing_capacity?: string | null } | null;
}): UnassessedReasonInfo[] {
  if (cell.unassessed_reason) {
    // ONE bucket per row (its single lead sentence, `describeUnassessed`),
    // not one per `kind:state` part (`unassessedReasonInfo`'s older,
    // multi-entry shape) -- a row with four parts now groups under the one
    // cause a practice should act on first, matching `WhereYouStand.tsx`'s
    // own per-row lead (2026-09-21 polish item 1).
    const d = describeUnassessed(cell.unassessed_reason, cell.patient_state,
      cell.prescribing_capacity ?? cell.display_context?.prescribing_capacity);
    return [
      {
        sentence: d.lead,
        action: d.action?.label ?? 'Nothing for you to do yet',
        actionSection: d.action?.section ?? null,
      },
    ];
  }
  if (cell.reason) {
    const parts = cell.reason.split(',').map((p) => p.trim()).filter(Boolean);
    const seen = new Set<string>();
    const out: UnassessedReasonInfo[] = [];
    for (const part of parts) {
      const info = scopeReasonInfo(part);
      if (!seen.has(info.sentence)) {
        seen.add(info.sentence);
        out.push(info);
      }
    }
    if (out.length > 0) return out;
  }
  return [
    {
      sentence: 'We have not reviewed this combination yet.',
      action: 'Nothing for you to do yet',
      actionSection: null,
    },
  ];
}

/** The group heading for "What we could not check" -- one bucket per
 *  distinct reason sentence, never per cell (a customer reads "add this
 *  credential" once, not forty times). */
export const COULD_NOT_CHECK_LEAD =
  'These checks were declared under Setup but could not run yet. Grouped by why, with the one action that moves each group.';
export const COULD_NOT_CHECK_NONE =
  'Nothing declared under Setup is waiting to be checked. Everything you have told us about has a result.';

// ---- W2: setup rail section strings ----------------------------------
// Lead sentences for the four rail stops that now wrap the standalone
// setup sections (practice / prescribers / prescribing / agreements),
// plus the strings "What we need from you" needs for its own readiness-gap
// list. Reuses SHELL_SECTION_LABEL / dataRequestNote rather than
// duplicating them -- only what was genuinely missing lives here.

export const YOUR_PRACTICE_LEAD =
  'Your practice identity, how you run visits, and the states your patients are located in when you see them — not your prescribers’ licence states or your own office address.';

export const PRESCRIBERS_LEAD =
  'Your roster and the licences, DEA registrations and state controlled-substance registrations behind it. Add people and credentials directly, or import a roster file below.';

export const PRESCRIBING_LEAD =
  'Three separate things: the medications you offer, the services you plan to offer by prescriber, state and medication, and what your own records show actually happened.';

export const AGREEMENTS_LEAD =
  'Supervisory and collaborative agreements linking real prescribers on your roster — who supervises whom, in which state, covering which medications, and for how long.';

export const IMPORT_ROSTER_TOGGLE_LABEL = 'Import a roster file';
export const IMPORT_ACTIVITY_TOGGLE_LABEL = 'Import an activity report';

// The file-drop bridge (BL-648 W8): a summarised upload becomes prescribers,
// credentials and activity to review, through the same import screen above.
export const FILE_BECAME_TITLE = 'What this file became';
export function fileBecameSummary(
  prescribers: number,
  credentials: number,
  activityRows: number,
  couldNotPlace: number,
): string {
  const parts: string[] = [];
  parts.push(`${prescribers} prescriber${prescribers === 1 ? '' : 's'}`);
  parts.push(`${credentials} credential${credentials === 1 ? '' : 's'}`);
  parts.push(`${activityRows} activity row${activityRows === 1 ? '' : 's'}`);
  let s = parts.join(', ');
  if (couldNotPlace > 0) {
    s += `, and ${couldNotPlace} row${couldNotPlace === 1 ? '' : 's'} we could not place`;
  }
  return s;
}
export const FILE_BECAME_NONE =
  'Nothing in this file matched a prescriber, credential or activity record we could carry over.';
// BL-707: an EHR export can name a prescriber by a login rather than a
// name; this line names how many of those we could not match to anyone on
// the roster (a single one reads oddly as "1 people", so it is worded
// around the count instead of pluralising a noun).
export function fileBecameNotOnRoster(notOnRoster: number): string | null {
  if (notOnRoster <= 0) return null;
  return notOnRoster === 1
    ? '1 person in your report is not on your roster.'
    : `${notOnRoster} people in your report are not on your roster.`;
}
// CPO46E: a file that names people who are not on the prescriber list ends in
// a next step, not a dead end: add them, then read the same file again (the
// original is still held), with no new upload.
export function fileUnlistedLead(n: number): string {
  return n === 1
    ? '1 person in this file is not on your prescriber list.'
    : `${n} people in this file are not on your prescriber list.`;
}
export function fileUnlistedAddThem(n: number): string {
  return n === 1 ? 'Add that person under' : 'Add them under';
}
export const FILE_UNLISTED_THEN_READ = ', then read this file again.';
export const FILE_READ_AGAIN_ACTION = 'Read this file again';
export const FILE_READING_AGAIN =
  'We are reading this file again. When it shows as read, choose “See what this file became”.';
export const FILE_READ_AGAIN_CHOICES_NOTE =
  'The product choices you made for this file will be asked again.';
export function fileBecameNoneUnlisted(n: number): string {
  return n === 1
    ? 'Nothing could be carried over yet, because the prescriber in this file is not on your prescriber list.'
    : 'Nothing could be carried over yet, because the prescribers in this file are not on your prescriber list.';
}
export const FILE_COULD_NOT_PLACE_HEADING = 'We could not place these';
export const REVIEW_PRESCRIBERS_ACTION = 'Review prescribers';
export const REVIEW_ACTIVITY_ACTION = 'Review activity';
export const FILE_REVIEW_DONE_CHECK_HINT =
  'Once you have added what you want from this file, come back here to check your setup.';

export const WHAT_WE_NEED_LEAD =
  'Everything your practice still needs to give or answer, in one list.';

// PR — short, explicit labels for the two distinct report histories.
export function reportHistoryLabel(managedEarlier: number, uploadedEarlier: number): string {
  const managed = `${managedEarlier} earlier ${managedEarlier === 1 ? 'result' : 'results'}`;
  return uploadedEarlier > 0
    ? `${managed} · ${uploadedEarlier} uploaded ${uploadedEarlier === 1 ? 'report' : 'reports'}`
    : managed;
}
export const REPORTS_PURPOSE = 'See your current results, the PDF report, and the compliance review status.';
/** The page's status chip: published results and downloadable reports are
 *  different things, each counted under its own word. */
export function reportsPageStatus(results: number, reports: number): string {
  if (results === 0) return 'No results yet';
  const r = `${results} ${results === 1 ? 'result' : 'results'}`;
  return reports > 0 ? `${r} · ${reports} ${reports === 1 ? 'report' : 'reports'}` : r;
}
export const REPORT_PDF_NOT_SIGNOFF =
  'This prepares a PDF for your lawyer. Compliance review and signature are separate.';
export const AGREEMENT_ONE_STATE_HELP =
  'Save one agreement for each state it covers. Choose the first state here, then repeat for any other states.';
export const AGREEMENT_HERO_HELP =
  'Start with the people below. Your recorded agreements are listed after them.';
export function agreementHero(people: number): string {
  return people > 0
    ? `${people} ${people === 1 ? 'prescriber has' : 'prescribers have'} agreement details to finish`
    : 'Your agreement details are recorded';
}

export const READINESS_GAPS_NONE =
  'Nothing is waiting on a missing document or record right now.';

export const READINESS_GAPS_HEADING = 'Documents we need';
// The short index at the top of Still needed (UXWALK P1-15).
export const INDEX_LABEL = 'On this page';
export const AGREEMENT_DRAWER_TITLE = 'Record the agreement';
export const INDEX_QUESTIONS = 'Questions';
export const INDEX_STATEMENTS = 'Statements';
export const INDEX_RENEWALS = 'Renewals';
export const INDEX_RECORDS = 'Files and records';

const EVIDENCE_KIND_WORD: Record<string, string> = {
  state_license: 'state licence',
  dea_registration: 'DEA registration',
  state_csr: 'state controlled-substance registration',
  prescriptive_authority: 'state prescribing authorization',
  ms_cspa: 'Mississippi controlled-substance prescribing approval',
  collaboration_agreement: 'supervision or collaboration agreement',
  pa_crnp_agreement: 'Pennsylvania CRNP prescriptive collaborative agreement checklist',
  ky_capa_ns: 'Kentucky agreement for non-controlled medications (CAPA-NS)',
  ky_capa_cs: 'Kentucky agreement for controlled substances (CAPA-CS)',
  ky_capa_ns_exemption: 'Kentucky exemption from the CAPA-NS',
  ky_capa_cs_exemption: 'Kentucky exemption from the CAPA-CS',
  wi_aprn_transition: 'Wisconsin APRN prescribing pathway and licence notation',
  az_pa_ii_iii_approval: 'Arizona PA Schedule II and III Board approval',
  al_schedule_ii_lpsp: 'Alabama Schedule II permit and approved formulary',
  la_aprn_site_cs_scope: 'Louisiana APRN practice-site and controlled-schedule approval',
  md_aprn_role_scope: 'Maryland APRN certified-role prescribing scope',
  nm_non_cnp_formulary: 'New Mexico CNS/CRNA role formulary',
  or_crna_outpatient_scope: 'Oregon CRNA outpatient prescription scope',
  agreement_exemption: 'answer on the state\'s own condition for practising without an agreement',
  supervisee_count: 'count of the nurse practitioners and physician assistants the supervising physician oversees in the state',
  provider_location_dea_registration: 'DEA registration at the prescriber’s work location',
  provider_location_state: 'state where the prescriber is working during these visits',
  compounding_pathway: 'medication formulation and compounding pathway',
  compounding_503b_ingredient_basis: 'ingredient basis for this medication from a 503B outsourcing facility',
  compounding_route_contradiction: 'what is actually dispensed for a medication marked not compounded',
  fl_no_office_or_in_person: 'no Florida office or in-person services under telehealth registration',
  prescribing_care_setting: 'Care setting for these prescriptions',
  prescribed_drug_use_site: 'Where prescribed medication will be used',
  fl_schedule_ii_exception: 'Florida Schedule II telehealth exception',
  out_of_state_telehealth_registration: 'out-of-state telehealth registration',
  fl_registration_exemption: 'Florida emergency or consultation registration exemption',
  encounter_modality: 'visit format',
  tx_relationship_basis: 'clinician–patient relationship for remote visits',
  tx_new_relationship_followup: 'follow-up for new remote relationships',
  tx_chronic_pain: 'chronic-pain prescribing',
  tx_established_pain_patient: 'established pain-patient status',
  tx_identical_repeat: 'identical repeat prescriptions',
  tx_qualifying_visit_90d: 'qualifying visits within 90 days',
  tx_exception_documented: 'recorded reason for a prescribing exception',
  tx_factors_considered: 'factors considered for a prescribing exception',
};

/** Same vocabulary as `unassessedPartInfo`'s private `CREDENTIAL_KIND_WORD`
 *  (not exported from there), for the readiness-gap groups on "What we need
 *  from you" -- kept as its own small map here rather than exporting and
 *  coupling two independent group builders to one shared constant. */
export function evidenceKindWord(kind: string): string {
  const [base, ...parameters] = kind.split(':');
  const baseWord = EVIDENCE_KIND_WORD[base] ?? base.replace(/_/g, ' ');
  if (parameters.length === 0) return baseWord;
  if (base === 'agreement_medication_scope')
    return `agreement medication coverage for ${parameters.join(' ').replace(/_/g, ' ')}`;
  return `${baseWord} for ${parameters.join(' ').replace(/_/g, ' ')}`;
}

/** The leading noun phrase for a plain (non-medication-scoped) evidence
 *  kind, capitalized and in the number a prescriber count calls for --
 *  "State licence"/"State licences", "DEA registration"/"DEA
 *  registrations", "Supervision or collaboration agreement(s)". Every word
 *  this map produces is already a regular plural (no irregular forms in
 *  `EVIDENCE_KIND_WORD` or its `_`-joined fallback). */
function evidenceKindWordLabel(kind: string, count: number): string {
  const word = evidenceKindWord(kind);
  // A label is never pluralised ("(CAPA-NS)s"): the count sits in the sentence around it.
  void count;
  return capitalizeFirst(word);
}

/** Keeps SetupShell's own, already-reviewed sentence verbatim (the exact
 *  phrasing `telehealth-evidence.test.mjs` was written against) rather than
 *  inventing new copy for the same fact now that it has moved to "What we
 *  need from you" -- only the leading count/prescriber phrase is new
 *  (BL-756: a group's raw row count is one per (prescriber, state, drug
 *  class) CELL, never one per credential -- see `groupGaps`' comment). */
export function readinessGapSendDocumentSentence(
  evidenceKind: string,
  checkCount: number,
  prescriberCount: number,
): string {
  const label = evidenceKindWordLabel(evidenceKind, prescriberCount);
  return `${label}: declared for ${prescriberCount} ${prescriberCount === 1 ? 'prescriber' : 'prescribers'} but not yet reviewed by VerityRx. Send us the licence document so we can confirm ${
    checkCount === 1 ? 'it' : 'them'
  } — go to "Send us files" to upload one.`;
}

/** C5FIXU: the visit-format facts a check can wait on, asked as the plain factual
 *  question they are (never "Encounter modality" or "Tx relationship basis"). They
 *  are edited on Prescribing (visit format) and under Still needed (the Texas
 *  workflow questions), not on a credential editor. */
export const VISIT_FORMAT_QUESTION: Record<string, string> = {
  provider_location_dea_registration: 'What DEA registration covers the prescriber’s work location? Record it under Prescribers for that state, including the schedules and principal location.',
  fl_registration_exemption: 'Is this Florida remote care for an emergency medical condition or in consultation with a Florida-licensed professional who has ultimate authority over diagnosis and care?',
  provider_location_state: 'In which state is the prescriber working during these visits?',
  compounding_pathway: 'Is this medication from a 503A pharmacy, a 503B facility, or a non-compounded product?',
  compounding_503b_ingredient_basis: 'For this 503B medication, does the facility use no bulk drug substances, a substance on the 503B bulks list, or a drug on the shortage list at compounding, distribution and dispensing? Record the ingredient basis under Prescribing.',
  compounding_route_contradiction: 'You marked this medication as not compounded, but our reference lists only a compounded route. What do you actually dispense, and from whom? Update it under Prescribing.',
  fl_no_office_or_in_person: 'Does this registered provider have no Florida office and provide no in-person services in Florida?',
  prescribing_care_setting: 'What type of care does this prescribing combination cover?',
  prescribed_drug_use_site: 'Will these prescriptions be used only within the health care facility where the prescriber practices?',
  fl_schedule_ii_exception: 'Which Florida Schedule II exception covers these remote prescriptions?',

  encounter_modality: 'How are these visits held: by video, by audio only or in person?',
  tx_relationship_basis: 'How was the relationship between the prescriber and each patient first established for remote visits?',
  tx_new_relationship_followup: 'Is there a follow-up visit when a new remote relationship begins?',
  tx_chronic_pain: 'Is this medication prescribed for chronic pain?',
  tx_established_pain_patient: 'Is the patient an established pain patient?',
  tx_identical_repeat: 'Is this an identical repeat of an earlier prescription?',
  tx_qualifying_visit_90d: 'Did the patient have a qualifying visit within the last 90 days?',
  tx_exception_documented: 'Is the reason for the prescribing exception documented?',
  tx_factors_considered: 'Were the required factors considered and recorded for the exception?',
};
export const isVisitFormatKind = (kind: string | null | undefined): boolean => !!kind && kind in VISIT_FORMAT_QUESTION;
/** Where the practice edits a visit-format fact. */
export const visitFormatSection = (kind: string): 'prescribing' | 'need' => (['provider_location_dea_registration', 'encounter_modality', 'provider_location_state', 'compounding_pathway', 'compounding_503b_ingredient_basis', 'compounding_route_contradiction', 'fl_no_office_or_in_person', 'fl_schedule_ii_exception', 'fl_registration_exemption', 'prescribed_drug_use_site', 'prescribing_care_setting'].includes(kind) ? 'prescribing' : 'need');

/** Same fact as above, now singular/plural-correct ("has"/"have"), and
 *  "needed" rather than "required" -- softer, and consistent with the rest
 *  of the surface's plain-question vocabulary (2026-09-22 review item 11).
 *
 *  BL-756: `checkCount` (the group's raw row count) and `prescriberCount`
 *  (`GapGroup.prescriberStates.size`, its distinct prescriber count) are
 *  DIFFERENT numbers for a plain evidence kind (state licence / DEA
 *  registration / state CSR / collaboration agreement) -- one licence
 *  blocks a check for every drug class a prescriber is scoped to, so AV's 5
 *  prescribers missing a state licence produced 386 blocked checks. The
 *  sentence leads with the honest, small number (prescribers) and states
 *  the larger number as what it actually counts (checks), never implying
 *  386 missing licences. An `agreement_medication_scope` group needs no
 *  such split: its evidence key is already scoped to one medication, so its
 *  row count IS its prescriber count (one row per prescriber per state). */
export function readinessGapNotRecordedSentence(
  kind: string,
  checkCount: number,
  prescriberCount: number,
  medicationName?: string,
): string {
  const visitQuestion = VISIT_FORMAT_QUESTION[kind];
  if (visitQuestion)
    return `${visitQuestion} ${checkCount} ${checkCount === 1 ? 'check waits' : 'checks wait'} on this answer.`;
  if (kind.startsWith('agreement_medication_scope:')) {
    const medication = medicationName?.trim() || kind.slice('agreement_medication_scope:'.length).replace(/_/g, ' ');
    return `${checkCount} agreement${checkCount === 1 ? '' : 's'} need${checkCount === 1 ? 's' : ''} to say whether ${checkCount === 1 ? 'it' : 'they'} cover ${medication}.`;
  }
  const word = evidenceKindWord(kind);
  if (kind.includes(':'))
    return `${checkCount} record${checkCount === 1 ? '' : 's'} for ${word} ${checkCount === 1 ? 'has' : 'have'} not been declared yet.`;
  const label = evidenceKindWordLabel(kind, prescriberCount);
  return `${label}: not on file yet for ${prescriberCount} ${prescriberCount === 1 ? 'prescriber' : 'prescribers'}. ${checkCount} ${checkCount === 1 ? 'check waits' : 'checks wait'} on ${checkCount === 1 ? 'it' : 'them'}.`;
}

// Labels for the setup sections' raw DB codes (brief 2026-09-21 polish item
// 2): every select that renders a stored vocabulary value shows a plain
// word from here, never the code itself (`unknown`, `professional_llc`,
// a bare `stated`/`no_stated_expiry`). `check-copy-words.mjs` gates the
// visible SPA literals; these maps are how a select stays off that list
// while still speaking the DB's own vocabulary underneath.
export const LOCATION_TYPE_LABEL: Record<string, string> = {
  dedicated_office: 'Dedicated office',
  coworking: 'Coworking',
  coworking_dedicated: 'Coworking, dedicated space',
  coworking_hotdesk: 'Coworking, hot desk',
  home_office: 'Home office',
  mail_forwarding: 'Mail forwarding',
  virtual_office: 'Virtual office',
  other: 'Other',
  unknown: 'Unknown',
  not_sure: 'Not sure',
};

export const EMPLOYMENT_TYPE_LABEL: Record<string, string> = {
  employee: 'Employee',
  contractor: 'Contractor',
  owner: 'Owner',
  other: 'Other',
  unknown: 'Unknown',
};

export const YES_NO_UNKNOWN_LABEL: Record<string, string> = {
  yes: 'Yes',
  no: 'No',
  unknown: 'Unknown',
};

export const CREDENTIAL_STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  expired: 'Expired',
  unknown: 'Unknown',
};

export const AGREEMENT_STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  inactive: 'Inactive',
  unknown: 'Unknown',
};

export const EXPIRATION_BASIS_LABEL: Record<string, string> = {
  stated: 'Stated date',
  no_stated_expiry: 'No stated expiry',
  unknown: 'Unknown',
};

export const WITHDRAWAL_REASON_CODE_LABEL: Record<string, string> = {
  entered_in_error: 'Entered in error',
  no_longer_held: 'No longer held',
  replaced_by_new_credential: 'Replaced by new credential',
  expired_not_renewed: 'Expired, not renewed',
  other: 'Other',
};

/** `PrescribingCapacity` is mostly real licence-type letters (MD/DO/NP/PA/
 *  CNM, already plain), with one raw code among them -- `unknown` -- that
 *  reads as a code rather than a word. */
export const PRESCRIBING_CAPACITY_LABEL: Record<string, string> = {
  unknown: 'Unknown',
};

/** The encounter modality, in words -- moved here from `ScopeSection.tsx`
 *  (2026-09-22 review item 6) so the results pages ("Where you stand",
 *  "What we could not check") render the SAME words Setup does, rather than
 *  a raw `audio_video`/`in_person` token with underscores turned to spaces
 *  (`humanizeAxis`'s old fallback). */
/** BL-715/717: what each assumption code on a result means, in the
 * customer's words. The binder says the same thing at greater length in
 * its opening "Assumptions to confirm" section. */
export const ASSUMPTION_WORDS: Record<string, string> = {
  work_location_dea_taken_as_declared: 'takes the work-state DEA registration as declared by the practice',
  provider_location_from_prescriber_declaration: 'uses the work state on the prescriber’s roster entry for these visits',
  compounding_pathway_from_medication_declaration: 'uses the compounding pathway declared for this medication',
  compounding_503b_basis_from_medication_declaration: 'uses the 503B ingredient basis declared for this medication',
  florida_registration_taken_as_declared: 'takes the Florida telehealth registration as declared by the practice',

  encounter_modality_from_practice_default:
    'assumes the visit happened the way your practice usually sees patients',
  prescribing_capacity_from_licence_type:
    "assumes the prescriber acted under the licence type on their roster entry",
  state_license_taken_as_declared:
    'takes the state licence as declared; nothing on file supports it yet',
  dea_registration_taken_as_declared:
    'takes the DEA registration as declared; nothing on file supports it yet',
  state_csr_taken_as_declared:
    'takes the state controlled-substance registration as declared; nothing on file supports it yet',
  collaboration_agreement_taken_as_declared:
    'takes the agreement as declared; no signed copy on file supports it yet',
  state_license_not_recorded_taken_as_absent:
    'treats the prescriber as holding no licence in this state, because none is on file',
  dea_registration_not_recorded_taken_as_absent:
    'treats the prescriber as holding no DEA registration here, because none is on file',
  state_csr_not_recorded_taken_as_absent:
    'treats the prescriber as holding no state controlled-substance registration here, because none is on file',
  collaboration_agreement_not_recorded_taken_as_absent:
    'treats the prescriber as having no agreement covering this state, because none is on file',
};

export function assumptionSentence(codes: readonly string[] | undefined): string | null {
  if (!codes || codes.length === 0) return null;
  const parts = codes.map((c) => ASSUMPTION_WORDS[c] ?? `rests on an assumption (${c})`);
  return `This result ${parts.join('; and ')}. Confirm it, or correct the record.`;
}

export const MODALITY_LABEL: Record<string, string> = {
  audio_video: 'Audio and video',
  audio_only: 'Audio only',
  in_person: 'In person',
  asynchronous: 'Asynchronous (store-and-forward)',
  unknown: 'Unknown',
};

// ---- BL-725: "Questions for you" (one-click answers) ---------------------
export const ANSWER_QUESTIONS_HEADING = 'Questions for you';

// BL-753: the practice landing reminder is a count-only projection of the
// same open sections. Destinations and counts come from the API.
export const STILL_NEEDED_HEADING = 'Still needed';
export const STILL_NEEDED_EMPTY = 'Nothing is waiting on you right now.';
export const STILL_NEEDED_LOADING = 'Loading…';
export const STILL_NEEDED_RETRY = 'Try again';
export const STILL_NEEDED_FILES = 'Send us files';

export const ANSWER_QUESTIONS_LEAD =
  'Your records leave these checks open. Answer yes, no or not sure. Each answer is saved as your practice’s own statement, with your name and today’s date, and the check runs again.';

export const ANSWER_QUESTIONS_SAVED =
  'Saved. Your answer is on record and the check runs again.';

export const ANSWER_QUESTIONS_RECHECKING = 'Re-checking…';

export const ANSWER_QUESTIONS_ALREADY =
  'Someone at your practice already answered one of these. We refreshed the list — please look again.';

export const ANSWER_AGREEMENT_ACTION = 'Add the agreement';
export const EDIT_AGREEMENT_ACTION = 'Edit agreement';

/** "Ohio", "Ohio and Texas", "Ohio, Texas and Utah". */
export function joinWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** The question row, e.g. "Does Jamie Rivera hold a state licence in Ohio and Texas?" */
export function answerQuestionSentence(name: string, kind: string, stateNames: string[]): string {
  const places = joinWords(stateNames);
  if (kind.startsWith('agreement_medication_scope:'))
    return `Does ${name}’s ${places} agreement cover ${kind.split(':')[1].replaceAll('_', ' ')}?`;
  if (kind === 'collaboration_agreement' || kind === 'pa_crnp_agreement' || kind.startsWith('ky_capa_'))
    return `Does ${name} have a ${evidenceKindWord(kind)} covering ${places}?`;
  return `Does ${name} hold a ${evidenceKindWord(kind)} in ${places}?`;
}

// L3 — question text that accompanies the state chips on What's left to do.
export function answerQuestionChipSentence(name: string, kind: string): string {
  if (kind.startsWith('agreement_medication_scope:'))
    return `Does ${name}’s agreement cover ${kind.split(':')[1].replaceAll('_', ' ')} in the states shown?`;
  if (kind === 'collaboration_agreement' || kind === 'pa_crnp_agreement' || kind.startsWith('ky_capa_'))
    return `Does ${name} have a ${evidenceKindWord(kind)} covering the states shown?`;
  return `Does ${name} hold a ${evidenceKindWord(kind)} in the states shown?`;
}

export function answerAllLabel(answer: 'yes' | 'no' | 'not_sure', count: number): string {
  const word = answer === 'yes' ? 'Yes' : answer === 'no' ? 'No' : ANSWER_NOT_SURE;
  return count === 1 ? word : count === 2 ? `${word} for both states` : `${word} for all ${count} states`;
}

export function answerSaveLabel(count: number): string {
  return `Save ${count} answer${count === 1 ? '' : 's'} and re-check`;
}

export function answerAgreementNote(): string {
  return 'An agreement needs the supervising prescriber named, so this one is answered on the agreements step.';
}

export const ANSWER_BY_STATE = 'Answer state by state';
// UIQ1 — grouped Ohio agreement medication scope prompt.
export const AGREEMENT_SCOPE_QUESTION = (name: string, state: string) => `Does ${name}’s ${state} agreement cover these medications?`;
export const AGREEMENT_SCOPE_YES_ALL = (count: number) => (count === 2 ? 'Yes, both' : `Yes, all ${count}`);
export const AGREEMENT_SCOPE_ONE_BY_ONE = 'Answer each medication';

/** The "Answer" jump beside a result that is a question to the practice. */
export const ANSWER_JUMP_LABEL = 'Answer';

// ---- "Where you stand" v2 (2026-09-23) -------------------------------------
// Written for a practice owner, an office manager or a physician reading in a
// hurry: one idea per sentence, their own words, no rule codes, no internal
// terms. Every count is a CHECK: one prescriber, one medication, one patient
// state -- said once, at the top of the page.

type StandBucket = 'fix' | 'answer' | 'confirm' | 'lawyer' | 'ours' | 'clear' | 'open';

/** The status words, the same on the tiles, the legend and every row. */
export const STAND_BUCKET_LABEL: Record<StandBucket, string> = {
  fix: 'Requirement not met',
  answer: 'Needs your answer',
  confirm: 'Needs your confirmation',
  lawyer: 'Needs a legal judgement',
  ours: 'Not yet reviewed by us',
  clear: 'Meets requirements',
  // TODO(Shawn): a finding the engine could not reach at all (no verdict) is a
  // different kind from "Not yet reviewed by us" (a state requirement we have
  // not read), so it keeps its own name rather than sharing that one.
  open: 'Not checked yet',
};

/** One short line under each tile: what the status means for the reader. */
export const STAND_BUCKET_HINT: Record<StandBucket, string> = {
  fix: 'Based on the records you gave us.',
  answer: 'A yes or no from you settles these.',
  confirm: 'Confirm how your practice works.',
  lawyer: 'Take this to your lawyer.',
  ours: 'Our review is not finished. Nothing for you to do.',
  clear: 'Nothing more to do.',
  open: 'Something is missing before we can check these.',
};

export const STAND_CHECK_DEFINITION =
  'A check is one prescriber, one medication and one patient state.';

export function checksWord(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'check' : 'checks'}`;
}

function peopleWord(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'prescriber' : 'prescribers'}`;
}

/** "Checked 23 Sep 2026 · 478 checks · 5 prescribers · 45 states". */
export function standMetaLine(p: {
  checkedOn: string;
  checks: number;
  prescribers: number;
  states: number;
}): string {
  return [
    `Checked ${p.checkedOn}`,
    checksWord(p.checks),
    peopleWord(p.prescribers),
    `${p.states.toLocaleString()} ${p.states === 1 ? 'state' : 'states'}`,
  ].join(' · ');
}

/** The answer to "am I okay?": one headline and at most two plain lines. */
export function standHeadline(p: {
  total: number;
  fix: number;
  /** Checks waiting on the practice: answers and confirmations. */
  input: number;
  /** Checks with the lawyer or with us -- nothing for the practice to do. */
  elsewhere: number;
  clear: number;
  fixStateNames: string[];
  /** Question rows, and how many checks they are needed for. */
  questions: number;
  questionChecks: number;
  notChecked: number;
}): { title: string; lines: string[] } {
  if (p.total === 0) {
    return p.notChecked > 0
      ? {
          title: 'We could not check anything yet.',
          lines: [
            `${p.notChecked.toLocaleString()} ${p.notChecked === 1 ? 'row' : 'rows'} from your records could not be matched to your practice. See why below.`,
          ],
        }
      : {
          title: 'Nothing has been checked yet.',
          lines: ['Finish setting up your practice, then check it.'],
        };
  }
  const lines: string[] = [];
  const questionTail =
    p.questions > 0
      ? ` ${p.questions.toLocaleString()} ${p.questions === 1 ? 'question covers' : 'questions cover'} ${
          p.questionChecks * 2 >= p.input ? 'most of them' : 'some of them'
        }.`
      : '';
  let title: string;
  if (p.fix > 0) {
    const n = p.fixStateNames.length;
    const where = n === 0 ? '' : n <= 3 ? `, in ${joinWords(p.fixStateNames)}` : `, across ${n} states`;
    title = `${checksWord(p.fix)} ${p.fix === 1 ? 'does not' : 'do not'} meet requirements${where}.`;
    if (p.input > 0)
      lines.push(
        `${p.input.toLocaleString()} more ${p.input === 1 ? 'needs' : 'need'} an answer or a confirmation from you.${questionTail}`,
      );
  } else if (p.input > 0) {
    title = 'Nothing fails on your records; answers are still needed.';
    lines.push(
      `${checksWord(p.input)} ${p.input === 1 ? 'is' : 'are'} waiting on you.${questionTail}`,
    );
  } else if (p.elsewhere > 0) {
    title = 'Nothing fails on your records, and nothing is waiting on you.';
    lines.push(
      `${checksWord(p.elsewhere)} ${p.elsewhere === 1 ? 'is' : 'are'} still with your lawyer or with us.`,
    );
  } else if (p.clear === 0) {
    // Every check is one we could not make. Never "meets requirements": a
    // check we could not run is not a check that passed.
    return {
      title: 'We could not check anything yet.',
      lines: [
        `${checksWord(p.notChecked)} could not be checked. See what is missing below.`,
      ],
    };
  } else if (p.clear < p.total || p.notChecked > 0) {
    title = 'Everything we could check meets requirements.';
    lines.push(`${checksWord(p.notChecked)} could not be checked yet. See what is missing below.`);
  } else {
    title = 'Everything we checked meets requirements.';
  }
  if (p.clear === p.total && p.notChecked === 0)
    lines.push(`All ${checksWord(p.total)} passed, based on what you told us.`);
  else if (p.clear > 0)
    lines.push(`${p.clear.toLocaleString()} of your ${checksWord(p.total)} already meet requirements.`);
  else
    lines.push(
      `None of your ${checksWord(p.total)} is cleared yet. Each one is waiting on at least one of the steps below.`,
    );
  return { title, lines };
}

/** Plain words for the requirements a telehealth practice meets most often.
 *  `title` names the requirement; `fix` says what to do; `step` is the
 *  practice-wide to-do when the rule stops on a fact about the practice.
 *  A rule without an entry falls back to the first sentence of its own
 *  statement, so a new rule is never unlabelled. Keyed by rule, never by
 *  customer. */
import { RULE_PLAIN } from '../shared/rulePlain.ts';
export { RULE_PLAIN } from '../shared/rulePlain.ts';

/** The first sentence of a rule statement, never cut inside brackets. */
export function firstSentence(statement: string | null | undefined): string {
  const s = (statement ?? '').trim();
  let depth = 0;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (ch === '.' && depth === 0 && (i === s.length - 1 || s[i + 1] === ' ')) {
      const head = s.slice(0, i + 1);
      // "Ky. Rev." / "N.Y." style abbreviations: keep reading.
      if (/\b[A-Z][a-z]?\.$/.test(head) || /\b[A-Z]\.[A-Z]\.$/.test(head)) continue;
      return head;
    }
  }
  return s;
}

export function ruleTitle(ruleCode: string | null | undefined, statement: string | null | undefined): string {
  const plainCode = ruleCode?.startsWith('portal_legal_conditions:') ? 'portal_legal_conditions' : ruleCode;
  return (plainCode && RULE_PLAIN[plainCode]?.title) || firstSentence(statement) || 'A requirement';
}

export function ruleFix(
  ruleCode: string | null | undefined,
  requiredAction: string | null | undefined,
): string | null {
  const plainCode = ruleCode?.startsWith('portal_legal_conditions:') ? 'portal_legal_conditions' : ruleCode;
  return (plainCode && RULE_PLAIN[plainCode]?.fix) || requiredAction || null;
}

/** The button under a problem, by where the engine says the fact lives. */
export const FIX_PLACE_ACTION: Record<'prescribers' | 'agreements' | 'practice' | 'prescribing', string> = {
  prescribers: 'Go to Prescribers',
  prescribing: 'Go to Prescribing',
  agreements: 'Go to Agreements',
  practice: 'Go to Your practice',
};

/** One "what is needed" phrase for a row of the full list. */
export function standNeedPhrase(n: {
  bucket: StandBucket;
  ruleCode: string | null;
  statement: string | null;
  pendingKinds: string[];
  unassessedLead?: string | null;
}): string {
  switch (n.bucket) {
    case 'fix':
      return `Missing: ${lowerFirst(ruleTitle(n.ruleCode, n.statement))}`;
    case 'answer': {
      const kinds = n.pendingKinds.map((k) => evidenceKindWord(k));
      return kinds.length
        ? `Tell us about their ${joinWords(kinds)} here`
        : 'Answer the question for this state';
    }
    case 'confirm':
      return `Confirm: ${lowerFirst(ruleTitle(n.ruleCode, n.statement))}`;
    case 'lawyer':
      return 'Your lawyer decides this one';
    case 'ours':
      return 'Our review of this is not finished';
    case 'open':
      return n.unassessedLead ?? 'Not checked yet';
    default:
      return 'Nothing';
  }
}

function lowerFirst(s: string): string {
  return s.length > 1 && !/^[A-Z]{2}/.test(s) && !/^(DEA|California|Texas)\b/.test(s)
    ? s[0].toLowerCase() + s.slice(1)
    : s;
}

// ---- Next steps -------------------------------------------------------------

export interface StepWords {
  title: string;
  detail: string;
  action?: string;
}

export function stepFixWords(checks: number, people: number, stateNames: string[]): StepWords {
  const where =
    stateNames.length === 0
      ? ''
      : stateNames.length <= 3
        ? ` · ${joinWords(stateNames)}`
        : ` · ${stateNames.length} states`;
  return {
    title: 'Fix what does not meet requirements',
    detail: `${checksWord(checks)} · ${peopleWord(people)}${where}. These fail on records you gave us.`,
    action: 'See what to fix',
  };
}

/** `stillNeeded` is the digest's question count (the number Still needed calls questions); when it is larger, say what this
 *  step counts against it, so the two numbers never read as a disagreement (C3FOLLOW). */
export function stepAnswerWords(questions: number, checks: number, stillNeeded?: number | null): StepWords {
  const of =
    stillNeeded && stillNeeded > questions
      ? `${questions.toLocaleString()} of the ${stillNeeded.toLocaleString()} questions are about licences. `
      : '';
  return {
    title: `Answer ${questions.toLocaleString()} ${questions === 1 ? 'question' : 'questions'} about licences`,
    detail: `${of}Needed to finish ${checksWord(checks)}. Each answer takes one click, and we re-check straight away.`,
    action: 'Answer now',
  };
}

export function stepAgreementWords(people: number, checks: number): StepWords {
  return {
    title: 'Add your collaboration and supervision agreements',
    detail: `${peopleWord(people)} prescribe under an agreement in some states. Needed to finish ${checksWord(checks)}.`,
    action: 'Add agreements',
  };
}

export function stepPracticeWords(ruleCode: string, statement: string, checks: number): StepWords {
  const plain = RULE_PLAIN[ruleCode];
  const where = plain?.field
    ? `In Your practice, answer “${plain.field}” `
    : 'One answer for your whole practice. ';
  // A rule without plain words must never put its legal sentence in the TITLE: a lower-cased
  // statute sentence ("virginia allows a controlled substance ...") read as broken on the live
  // page (2026-09-23). The sentence goes in the detail, where it reads as the requirement.
  return {
    title: plain?.step ?? 'Confirm a state requirement for your practice',
    detail: plain?.step
      ? `${where}It affects ${checksWord(checks)}.`
      : `${firstSentence(statement)} ${where}It affects ${checksWord(checks)}.`,
    action: 'Go to Your practice',
  };
}

/** All practice-address items as one step, naming each address wanted. */
export function stepAddressWords(ruleCodes: string[], checks: number): StepWords {
  const what = ruleCodes.map((c) => lowerFirst(RULE_PLAIN[c]?.title ?? 'a practice address'));
  return {
    title: ruleCodes.length === 1 ? (RULE_PLAIN[ruleCodes[0]]?.step ?? 'Add your practice’s address') : 'Add your practice’s addresses, or tell us it has none',
    detail: `We need ${joinWords(what)}. It affects ${checksWord(checks)}.`,
    action: 'Go to Your practice',
  };
}

/** BL-729: "J Taggart (Jtaggart6): 74 rows; D Hudson: 27 rows; and 3 others".
 *  Semicolons, because a label can carry its own brackets and commas. */
export function namedPeopleList(people: { label: string; rows: number }[], cap = 4): string {
  const shown = people
    .slice(0, cap)
    .map((p) => `${p.label}: ${p.rows.toLocaleString()} ${p.rows === 1 ? 'row' : 'rows'}`);
  const more = people.length - shown.length;
  if (more > 0) return `${shown.join('; ')}; and ${more} ${more === 1 ? 'other' : 'others'}`;
  return shown.length > 1 ? `${shown.slice(0, -1).join('; ')}; and ${shown[shown.length - 1]}` : (shown[0] ?? '');
}

export function stepRosterWords(rows: number, people: { label: string; rows: number }[] = []): StepWords {
  if (people.length > 0)
    return {
      title: `Match ${people.length === 1 ? 'the person' : `the ${people.length} people`} named in your file`,
      detail: `${namedPeopleList(people)}. ${rows.toLocaleString()} ${rows === 1 ? 'row' : 'rows'} of your prescribing file could not be checked until they are on your prescriber list. Match them under Prescribing; no re-import is needed.`,
      action: 'Match these people',
    };
  return {
    title: 'Match the people named in your file',
    detail: `${rows.toLocaleString()} ${rows === 1 ? 'row' : 'rows'} in your prescribing file ${rows === 1 ? 'names' : 'name'} someone who is not on your prescriber list, so we could not check ${rows === 1 ? 'it' : 'them'}. Match them under Prescribing; no re-import is needed.`,
    action: 'Match these people',
  };
}

export function stepLawyerWords(checks: number): StepWords {
  return {
    title: `${checksWord(checks)} ${checks === 1 ? 'needs' : 'need'} a legal judgement`,
    detail: 'Take this to your lawyer.',
  };
}

export function stepOursWords(checks: number): StepWords {
  return {
    title: `We are still reviewing ${checksWord(checks)}`,
    detail: 'Nothing for you to do. These update on their own when our review is done.',
  };
}

export const STEPS_HEADING = 'Your next steps';
export const STEPS_NONE = 'Nothing to do right now.';

// ---- "Based on what you told us" -----------------------------------------------

export const ASSUMPTIONS_HEADING = 'Based on what you told us';
export const ASSUMPTIONS_LEAD =
  'Your records never cover everything, so we filled a few gaps using your own answers rather than leave checks unanswered. If any of these is wrong, correct it and check again.';

/** One plain sentence per assumption, stated once for the whole page. */
export const ASSUMPTION_SUMMARY: Record<string, { text: string; action: string; section: SectionKey }> = {
  encounter_modality_from_practice_default: {
    text: 'Visits happen the way you told us your practice usually sees patients.',
    action: 'Change it in Your practice',
    section: 'practice',
  },
  prescribing_capacity_from_licence_type: {
    text: 'Each prescriber prescribes under the licence type on your roster, for example as a nurse practitioner.',
    action: 'Review your prescribers',
    section: 'prescribers',
  },
  state_license_not_recorded_taken_as_absent: {
    text: 'Where you listed no licence for a state, we took it that the prescriber has none there. That is why we ask you about those states.',
    action: 'Answer the questions',
    section: 'need',
  },
  collaboration_agreement_not_recorded_taken_as_absent: {
    text: 'Where no collaboration or supervision agreement is on file, we took it that there is none.',
    action: 'Add your agreements',
    section: 'agreements',
  },
  pa_crnp_agreement_not_recorded_taken_as_absent: {
    text: 'Pennsylvania CRNP agreement details have not all been recorded, so we ask you to complete the checklist.',
    action: 'Complete the agreement checklist',
    section: 'agreements',
  },
  ky_capa_ns_not_recorded_taken_as_absent: { text: 'The Kentucky agreement for non-controlled medications (CAPA-NS) is not fully recorded.', action: 'Complete the agreement', section: 'agreements' },
  ky_capa_cs_not_recorded_taken_as_absent: { text: 'The Kentucky agreement for controlled substances (CAPA-CS) is not fully recorded.', action: 'Complete the agreement', section: 'agreements' },
  ky_capa_ns_exemption_not_recorded_taken_as_absent: { text: 'No answer is recorded on whether Kentucky\u2019s exemption from the agreement applies (non-controlled medications).', action: 'Answer the exemption question', section: 'agreements' },
  ky_capa_cs_exemption_not_recorded_taken_as_absent: { text: 'No answer is recorded on whether Kentucky\u2019s exemption from the agreement applies (controlled substances).', action: 'Answer the exemption question', section: 'agreements' },
  dea_registration_not_recorded_taken_as_absent: {
    text: 'Where no DEA registration is on file for a state, we took it that there is none.',
    action: 'Review your prescribers',
    section: 'prescribers',
  },
  state_csr_not_recorded_taken_as_absent: {
    text: 'Where no state controlled-substance registration is on file, we took it that there is none.',
    action: 'Review your prescribers',
    section: 'prescribers',
  },
  prescriptive_authority_not_recorded_taken_as_absent: {
    text: 'Where no separate state prescribing authorization is on file, we asked whether the prescriber holds one.',
    action: 'Answer the question',
    section: 'prescribers',
  },
  ms_cspa_not_recorded_taken_as_absent: {
    text: 'Where no Mississippi controlled-substance prescribing approval is on file, we asked whether the prescriber holds one.',
    action: 'Answer the question',
    section: 'prescribers',
  },
  state_license_taken_as_declared: {
    text: 'Where you told us a licence is held but we have no copy, we took your word for it.',
    action: 'Send us a copy',
    section: 'files',
  },
  dea_registration_taken_as_declared: {
    text: 'Where you told us a DEA registration is held but we have no copy, we took your word for it.',
    action: 'Send us a copy',
    section: 'files',
  },
  state_csr_taken_as_declared: {
    text: 'Where you told us a state controlled-substance registration is held but we have no copy, we took your word for it.',
    action: 'Send us a copy',
    section: 'files',
  },
  prescriptive_authority_taken_as_declared: {
    text: 'Where you told us a separate state prescribing authorization is held but we have no copy, we took your word for it.',
    action: 'Send us a copy',
    section: 'files',
  },
  ms_cspa_taken_as_declared: {
    text: 'Where you told us the Mississippi controlled-substance prescribing approval is held but we have no copy, we took your word for it.',
    action: 'Send us a copy',
    section: 'files',
  },
  collaboration_agreement_taken_as_declared: {
    text: 'Where you told us an agreement is in place but we have no signed copy, we took your word for it.',
    action: 'Send us a copy',
    section: 'files',
  },
  pa_crnp_agreement_taken_as_declared: {
    text: 'We used your Pennsylvania CRNP agreement checklist as declared pending a signed copy.',
    action: 'Send us the agreement',
    section: 'files',
  },
};

/** Some assumptions arrive already written as a sentence (the Texas chronic-pain default: "Taken
 *  as not chronic-pain treatment: Modafinil is not a pain medication"). */
const CHRONIC_PAIN_ASSUMPTION = /^Taken as not chronic-pain treatment: (.+) is not a pain medication\.?$/;

export function assumptionSummaryText(code: string): string {
  if (Object.prototype.hasOwnProperty.call(PRODUCT_DECLARATION_MESSAGES, code)) return PRODUCT_DECLARATION_MESSAGES[code];
  const s = ASSUMPTION_SUMMARY[code];
  if (s) return s.text;
  const words = ASSUMPTION_WORDS[code];
  if (words) return `This result ${words}.`;
  const chronic = CHRONIC_PAIN_ASSUMPTION.exec(code);
  if (chronic) return `We took it that ${chronic[1]} is not used to treat chronic pain.`;
  // Any other already-written sentence is shown as written, never replaced by a generic line.
  if (code.includes(' ')) return `We took it that: ${code.replace(/\.$/, '')}.`;
  return 'This result rests on an assumption from your own records.';
}

export interface AssumptionEntry {
  key: string;
  /** The sentence to show; for a group, one sentence naming every member. */
  text: string;
  checks: number;
  /** The assumption code when this entry is one code, else null (a group). */
  code: string | null;
  /** Where to review it, when it is a group with no summary entry of its own. */
  groupAction?: { action: string; section: SectionKey };
}

/** One entry per assumption, with the eleven Texas chronic-pain defaults folded into ONE line that
 *  names each medication (PM-48), so the page never lists the same kind of assumption a dozen times. */
export function groupAssumptions(list: readonly { code: string; checks: number }[]): AssumptionEntry[] {
  const out: AssumptionEntry[] = [];
  const chronic: { name: string; checks: number }[] = [];
  for (const a of list) {
    const m = CHRONIC_PAIN_ASSUMPTION.exec(a.code);
    if (m) chronic.push({ name: m[1], checks: a.checks });
    else out.push({ key: a.code, text: assumptionSummaryText(a.code), checks: a.checks, code: a.code });
  }
  if (chronic.length === 1) {
    out.push({ key: 'chronic-pain', text: `We took it that ${chronic[0].name} is not used to treat chronic pain.`, checks: chronic[0].checks, code: null, groupAction: { action: 'Review the medications', section: 'prescribing' } });
  } else if (chronic.length > 1) {
    const names = chronic.map((c) => c.name).sort((x, y) => x.localeCompare(y));
    out.push({
      key: 'chronic-pain',
      text: `We took it that ${names.length} medications are not used to treat chronic pain: ${names.join(', ')}.`,
      checks: chronic.reduce((n, c) => n + c.checks, 0),
      code: null,
      groupAction: { action: `Review all ${names.length}`, section: 'prescribing' },
    });
  }
  return out.sort((a, b) => b.checks - a.checks || a.key.localeCompare(b.key));
}

// ---- The rest of the page -----------------------------------------------------

export const PROBLEMS_HEADING = 'What does not meet requirements';
export const PROBLEMS_LEAD =
  'Each of these fails on the records you gave us. Fix the record, or tell us if it is wrong, and check again.';
export const PEOPLE_HEADING = 'Your prescribers';
export const PEOPLE_LEAD = 'Where each prescriber stands, state by state.';
export const NOT_CHECKED_HEADING = 'What we could not check';
export const EVERY_CHECK_HEADING = 'Every check';
export const EVERY_CHECK_LEAD =
  'The full list, one row per prescriber, medication and state. Filter it, or download it as a spreadsheet.';
export const CSV_DOWNLOAD_LABEL = 'Download as a spreadsheet';
export const CHECK_AGAIN_LABEL = 'Check again';
export const CHECKING_NOW = 'Checking now. This usually takes under a minute.';
export const CHECK_SETTLING = "We'll check again once you pause — about a minute.";
export const CHECK_SETTLING_HEADING = 'Your first check is queued';
export const CHECK_SETTLING_ACTION = 'Waiting…';
export const IMPORT_CHECK_FAILED =
  'Your import was saved, but we could not queue its check. Use Check again under Where you stand.';
export const CHECK_FAILED_NOTE = 'The last check did not finish. Your previous result is unchanged.';
export const CHECK_UNCHANGED =
  'Nothing has changed since your last check, so this is still your current result. Update your setup or answer a question, then check again.';

/** BL-672: the pinned name, or an honest "not on file" naming the stable id
 *  -- never an invented clinician. */
export function prescriberLabel(name: string | null | undefined, id: string | null | undefined): string {
  if (name) return name;
  return id ? `Not on file · id ${id}` : 'A prescriber not on your roster';
}
export const CHECK_STILL_RUNNING =
  'Still working. This page has stopped checking automatically — reload to see whether it finished.';
export const STAND_STALE_NOTE =
  'You have changed your setup since this check. Check again to see where you stand now.';
export const ABOUT_HEADING = 'About these results';
export const ABOUT_TEXT =
  'We check the records you entered and the files you sent against each state’s official rules. This is not legal advice. A lawyer should confirm the results in your report before you rely on them.';
/** The examination workflow, in the practice's words -- the Your practice
 *  form and the results page's inline answer read this one table. */
export const GFE_WORKFLOW_LABEL: Record<string, string> = {
  synchronous_audio_video: 'Live audio-video visit',
  synchronous_audio_only: 'Live audio-only visit',
  async_questionnaire_only: 'Questionnaire only, no live visit',
  in_person_first: 'In-person visit first',
  mixed: 'A mix, depending on the visit',
  unknown: 'Unknown',
};

/** The inline exam question on the results page (BL-732). */
export const EXAM_QUESTION = 'How do you examine a new patient before prescribing?';
export const EXAM_SAVE_LABEL = 'Save and check again';
export const EXAM_SAVED = 'Saved. Checking again — your results update in about a minute.';
export const EXAM_NOT_ENOUGH =
  'A questionnaire alone, an audio-only call or a mix does not meet this requirement in states that require an examination, so it stays open.';

/** The summary tiles. Answers and confirmations share one tile -- both are
 *  the practice's to close -- and its hint splits them. */
export const TILE_INPUT_LABEL = 'Needs your answer or confirmation';
/** Counts the reader's ACTIONS, never checks: "328 answers and 63
 *  confirmations" read as 391 things to do when a handful of steps settle
 *  them (2026-09-23 screenshot review). */
export function tileInputHint(actions: number): string {
  if (actions <= 0) return 'Nothing is waiting on you.';
  return `${actions} ${actions === 1 ? 'step' : 'steps'} below ${actions === 1 ? 'clears' : 'clear'} these.`;
}
export const TILE_ELSEWHERE_LABEL = 'With your lawyer or us';
export const TILE_NOT_CHECKED_HINT = 'Rows we could not match to your practice.';

/** A prescriber's licence type in words. */
export const LICENCE_TYPE_WORD: Record<string, string> = {
  MD: 'Physician (MD)',
  DO: 'Physician (DO)',
  NP: 'Nurse practitioner',
  PA: 'Physician assistant',
  CNM: 'Certified nurse-midwife',
  CNS: 'Clinical nurse specialist',
  CRNA: 'Certified registered nurse anesthetist',
  RN: 'Registered nurse',
};

export function prescriberStatesLine(states: number, checks: number, medications: number): string {
  return `${states} ${states === 1 ? 'state' : 'states'} · ${medications} ${medications === 1 ? 'medication' : 'medications'} · ${checksWord(checks)}`;
}

export const SOURCES_NOTE =
  'Every requirement here comes from an official government source. Your report lists each one in full, with a link.';

// L1 page copy: local additions for the practice and prescribing surfaces.
export const L1_YOUR_PRACTICE_LEAD =
  'Your practice identity, how you run visits, and the states your patients are located in when you see them — not your prescribers’ licence states or your own office address.';
export const PRACTICE_ANSWER_HERO =
  'First, answer how you examine a new patient before prescribing.';
export function practiceAnswerStatus(answers: number): string {
  return `${answers} of 5 care-process answers given`;
}
export const L1_PRESCRIBING_LEAD =
  'See the medications you offer, the prescribing combinations you have saved, and activity from your records.';
export const L1_GFE_WORKFLOW_LABEL: Record<string, string> = {
  ...GFE_WORKFLOW_LABEL,
  unknown: 'Not answered',
};

// L2 — Prescribers + Agreements
export function prescriberRosterStatus(people: number): string {
  return `${people} prescriber${people === 1 ? '' : 's'}`;
}
/** Licence status has one home: Licences and deadlines (COHERE2 P1-20). */
export function prescriberLicenceStatusLink(expired: number, expiring: number): string {
  return expired || expiring
    ? `${expired} expired · ${expiring} ${expiring === 1 ? 'expires' : 'expire'} within 90 days. See Licences and deadlines`
    : 'See licence status on Licences and deadlines';
}
export function prescriberRenewalCounts(expired: number, expiring: number): string {
  return [expired ? `${expired} expired` : '', expiring ? `${expiring} ${expiring === 1 ? 'renewal' : 'renewals'} due` : ''].filter(Boolean).join(' · ');
}
export function prescriberMissingLicenceSummary(people: number): string {
  return `${people} prescriber${people === 1 ? '' : 's'} ${people === 1 ? 'has' : 'have'} no licence on file for one or more states. Review the roster, then answer the licence questions or add the document.`;
}
export const REVIEW_LICENCE_QUESTIONS = 'Review licence questions';
export function prescriberLicenceCount(states: number): string {
  return `Licence on file in ${states} state${states === 1 ? '' : 's'}`;
}
export function prescriberRegistrationCounts(dea: number, csr: number): string {
  return `${dea} DEA registration${dea === 1 ? '' : 's'} · ${csr} state controlled-substance registration${csr === 1 ? '' : 's'}`;
}
export function expiredLicenceFlag(state: string, date: string | null): string {
  return `Expired ${state}${date ? ` · ${date}` : ''}`;
}
export function expiringLicenceFlag(state: string, date: string): string {
  return `Expires within 90 days · ${state} · ${date}`;
}
export function missingLicenceFlag(states: number): string {
  return `No licence on file for this prescriber in ${states} state${states === 1 ? '' : 's'}:`;
}
/** A licence is on file but is not active (suspended, pending, inactive). */
export function inactiveLicenceFlag(state: string): string {
  return `The licence on file for ${state} is not active.`;
}
/** A licence IS on file for the state but it expired: say so, with the date
 *  (never "not on file" for a record the practice sent; FXBINDER hand-off). */
export function lapsedLicenceFlag(state: string, date: string | null): string {
  return date
    ? `The licence on file for ${state} expired on ${formatCalendarDate(date)}.`
    : `The licence on file for ${state} has expired.`;
}
export const REVIEW_QUESTIONS = 'Review questions';
export function credentialVerificationLabel(verifiedAt: string | null): string | null {
  if (!verifiedAt) return null;
  const date = new Date(verifiedAt);
  if (!Number.isFinite(date.getTime())) return null;
  const readableDate = formatViewerDate(verifiedAt, { dateStyle: 'long' });
  return `Verified against the board register on ${readableDate}.`;
}
// BL-655/R14: what the issuing board's own public record shows, for a
// credential this stored record disagrees with. A NOTICE only -- the
// engine still decides, and "Confirmed with the board" never means the
// engine has re-run.
export function boardCheckDisagreementNotice(
  notice: { board_name: string | null; board_status: string | null },
  ours: { status: string; expiration_date: string | null },
): string {
  const who = notice.board_name ? notice.board_name : 'the record on file';
  const boardStatus = notice.board_status ?? 'a different status';
  const ourStatus = ours.status === 'active' ? 'active' : ours.expiration_date
    ? `active until ${formatCalendarDate(ours.expiration_date)}`
    : ours.status;
  return `The board shows "${boardStatus}" for ${who}; your record says ${ourStatus}.`;
}
export function prescriberEvidenceSummary(level: 'primary_source' | 'document_supported' | 'self_declared' | 'none'): string {
  if (level === 'primary_source') return 'At least one credential was checked against an official board register.';
  if (level === 'document_supported') return 'At least one credential has a document on file, reviewed by VerityRx.';
  if (level === 'self_declared') return 'Declared by your practice — send documents to strengthen this.';
  return 'No credential evidence on file yet.';
}
export function agreementsNeedLine(states: number): string {
  return `No agreement on file for this prescriber in ${states} state${states === 1 ? '' : 's'}`;
}
export function agreementPrescriberStatus(people: number): string {
  return `${people} prescriber${people === 1 ? '' : 's'} ${people === 1 ? 'has' : 'have'} no agreement on file in one or more states`;
}
export const AGREEMENT_SAVED_RECHECK = 'Agreement saved. Your current results may still reflect the earlier details while the new check runs.';
export function agreementRecheckError(detail: string): string {
  return `Agreement saved, but the new check could not be requested: ${detail}`;
}
export const AGREEMENT_NOT_ANSWERED = 'Not answered';
export const RESULTS_HELD = HELD_WORDS;
export const ANSWER_NOT_SURE = 'Not sure';
export const AGREEMENT_SCOPE_NOT_SURE_ALL = (_count: number) => 'Not sure';
export const UNSURE_QUESTIONS_HEADING = "You're not sure about these";
export const UNSURE_QUESTIONS_NOTE = 'These questions are still open. Check the record, then update your answer.';
export const OFFICIAL_RECORD_LOOKUP = 'Official record look-up';
export const DEFAULT_LOOKUP_AUTHORITY = 'Official board';
export const NO_READABLE_LOOKUP = 'No readable official destination is available.';
export const PRESCRIBING_SCOPE_WORDS = {
  reviewAssumptions: 'Review assumptions',
  editScope: 'Edit prescribing scope',
  medicationsInScope: 'medications in scope',
  medicationsFromScope: (count: number) => `${count} medication ${count === 1 ? 'category is' : 'categories are'} saved from your file but not marked as offered in the list below.`,
  markOffered: (count: number) => `Mark these ${count} as offered`,
  saveMedicationEditsFirst: 'Save or discard your medication edits first.',
  assumedMode: (mode: string, count: number) => `We assumed ${mode ? mode[0]!.toLowerCase() + mode.slice(1) : mode} visits (your practice's default) for ${count} saved combinations.`,
  missingMode: (count: number) => `${count} saved combinations need a visit type. Set how you usually see patients under Your practice.`,
  assumedCapacity: (count: number) => `We assumed each prescriber prescribes as their recorded licence type for ${count} saved combinations.`,
  missingCapacity: (count: number) => `${count} saved combinations need a prescribing capacity because no licence type is on file.`,
  confirm: 'Confirm',
  change: 'Change…',
  chooseCapacity: 'Choose capacity',
  chooseVisitMode: 'Choose visit mode',
  visitMode: 'Visit mode',
  practiceLegalName: 'Practice legal name',
  applyTo: 'Apply to',
  allPrescribers: 'All prescribers',
  saveVisitMode: 'Save visit mode',
  saveVisitModeAndDefault: 'Save visit mode and practice default',
  prescribingCapacity: 'Prescribing capacity',
  saveCapacity: 'Save capacity for this prescriber',
  assumedValue: (value: string) => `${value} · assumed`,
} as const;
export function officialRecordLookupLabel(authority: string, states: string[]): string {
  return `${OFFICIAL_RECORD_LOOKUP}: ${authority} (${states.join(', ')})`;
}
export const LOOKUP_UNAVAILABLE_REASON = (reason: string) => `Look-up unavailable: ${reason}`;
export const HELD_QUESTIONS_LINE = HELD_SENTENCE;
export const ADD_LICENCE_DETAILS = 'Add the licence details';
export const sendCredentialDocument = (kind: string, state: string) => `Send a document for ${kind} in ${state}.`;
export const answerPrescriberQuestions = (name: string) => `Answer ${name}'s questions`;
export const checksSettledByAnswer = (count: number) => `${count} ${count === 1 ? 'check' : 'checks'} this answer can settle`;

/** Saved activity identity choices are inputs to the next deterministic check. */
export const ACTIVITY_MATCH_WORDS = {
  heading: 'Match these people',
  intro: 'Your imported rows stay as submitted. Matching each source name changes who the next check uses for those rows. No re-import is needed.',
  rowsCan: (n: number) => `${n} ${n === 1 ? 'row' : 'rows'} can be recovered.`,
  saved: (n: number, isNew: boolean) => `${n} ${n === 1 ? 'row now belongs' : 'rows now belong'} to ${isNew ? 'the new' : 'that'} prescriber. No re-import is needed. Results are waiting for the next check.`,
  undone: (n: number) => `${n} ${n === 1 ? 'row is' : 'rows are'} unmatched again. No re-import is needed. Results are waiting for the next check.`,
  missingFields: 'Enter this prescriber’s first name, last name, and licence type.',
  stale: 'These details changed in another tab. We refreshed them; review and try again.',
  chooseRoster: 'Match to a prescriber on your roster',
  choosePerson: 'Choose a prescriber',
  match: (n: number) => `Match ${n} ${n === 1 ? 'row' : 'rows'}`,
  add: 'Add as a new prescriber',
  addAndMatch: (n: number) => `Add and match ${n} ${n === 1 ? 'row' : 'rows'}`,
  firstName: 'First name',
  lastName: 'Last name',
  licenceType: 'Licence type',
  choose: 'Choose',
  undo: 'Undo match',
};

export const CHOOSE_EXACT_FACT = 'Choose the exact fact';
export const UNMATCHED_ACTIVITY_WORDS = {
  explanation: (n: number) => `${n} ${n === 1 ? 'row names' : 'rows name'} someone who is not on your roster. Match each person under Prescribing. No re-import is needed.`,
  action: 'Match these people',
};

export const MEDSPA_BINDER_PUBLISH_WORDS = {
  heading: 'Send to compliance review',
  loading: 'Checking the pending binder…',
  notReady: 'The practice has more to complete before a binder can be built.',
  notBuilt: 'The practice’s binder builds automatically each week; there is no new binder to send to compliance review.',
  publishingExplainer: 'Sending this binder starts counsel’s review of it.',
  withCounsel: (name: string | null, day: string | null) =>
    `${name ? `Binder ${name}` : 'This binder'} is with counsel for review${day ? `, sent ${day}` : ''}. The next build of the practice’s details can be sent once it exists.`,
  version: (name: string | null) => name ? `Compare with Binder ${name}` : 'First binder for this practice',
  change: (n: number) => n > 0 ? `+${n} from the current binder` : `${n} from the current binder`,
  audit: (ok: boolean) => ok ? 'Audit passed' : 'Audit needs attention',
  exportGate: (ok: boolean) => ok ? 'Export check passed' : 'Export check needs attention',
  findingsChanged: (added: number, removed: number, changed: number) =>
    `${added} new findings · ${removed} removed · ${changed} with a changed result`,
  warnings: 'Audit warnings',
  publish: 'Send to compliance review',
  publishing: 'Sending…',
  blocked: 'Sending is available after the audit and export checks pass.',
  buildNow: 'Build now',
  buildAgain: 'Build again',
  building: 'Building the binder… this takes a few minutes. This page updates when it is done.',
  built: (day: string | null) => day ? `Built ${day}.` : 'Built.',
  failed: (reason: string) => `The build failed. ${reason}`.trim(),
  alreadyPublished: 'This binder was already sent.',
  published: (name: string) => `Binder ${name} sent to compliance review.`,
} as const;

export const MANAGED_BINDER_FAILURE_WORDS: Record<string, string> = {
  citation_read_date_missing: 'A cited source has no recorded last-read date. Verify its source date, then rebuild the binder.',
  citation_source_unverified: 'A cited source did not pass the official text check. Recheck the source, then rebuild the binder.',
  citation_pins_missing: 'A rule behind this report names an official source that our citation library holds no recorded copy of, so the report cannot cite it. Nothing the practice entered caused this. Publish the missing source from the checked quote report, run the check again, then rebuild the report.',
};

export function signedReportLine(name: string, at: string, undecided?: number | null): string {
  const date = formatViewerDate(at);
  const status = undecided
    ? `Signed with ${undecided} ${undecided === 1 ? 'finding' : 'findings'} undecided. `
    : 'Signed. ';
  return `${status}Signed by ${name} on ${date}.`;
}

export const OWNER_BRIEF_LINK = 'Two-page summary';

export const RELEASE_IDENTITY_WORDS = {
  loading: 'Checking the rules in this release…',
  matches: 'Release data matches the database.',
  mismatch: 'Release data does not match the database. Hold assessments until the reviewed data import is complete.',
  unknown: 'The database release identity has not been recorded.',
  unavailable: 'Release identity is unavailable.',
} as const;

export function declaredCredentialResultLabel(verdict: string | null | undefined, assumptions?: string[] | null): string | null {
  return verdict === 'compliant' && assumptions?.some((item) => item.endsWith('_taken_as_declared'))
    ? 'Meets the requirement — based on your declaration'
    : null;
}

export const LAW_CHANGES_WORDS = {
  title: 'Changes in the law',
  purpose: 'Laws and rules that are new or changing in your states, and which of your checks and prescribers each one touches.',
  empty: 'No page change touching your current checks is recorded here.',
  watchLimit: 'A source page changing does not by itself mean the law changed. An interpretation appears only after counsel accepts a written note.',
  sourceLink: 'Read the current source',
  observed: 'Observed',
  reviewedHeading: 'What it means for you',
  openReviewed: 'Read counsel-reviewed note',
  reviewedAttribution: 'Counsel reviewed this note',
  digest: (pages: number, upcoming: number) => `${pages} ${pages === 1 ? 'page change touches' : 'page changes touch'} your current checks.${upcoming ? ` ${upcoming} upcoming rule ${upcoming === 1 ? 'change is' : 'changes are'} listed too.` : ''}`,
  shown: (visible: number, total: number) => `Showing ${visible} of ${total} law-update entries`,
  more: 'Show 25 more law-update entries',
  // R22 (BL-648, 29 Sep customer meeting): who, where and what a change
  // affects, in the practice's own words.
  affectsHeading: 'Affects you',
  affectsFilterAll: 'All',
  affectsFilterOurs: 'Affects us',
  affectsNobody: 'This does not rest on any of your current checks.',
  affectsProviders: (n: number) => `${n} ${n === 1 ? 'prescriber' : 'prescribers'}`,
  affectsShowNames: 'Show who',
  affectsHideNames: 'Hide names',
  affectsMedications: 'Medications',
  upcomingTransition: 'A rule already on the books starts to apply on a known date.',
  effectiveOn: 'Effective',
};

export const LICENCE_PLAN_WORDS = {
  costHeading: 'Plan the licence gaps',
  costLead: 'Compare the state fees and record what your practice plans to do.',
  heading: 'Choose a next step for this state',
  lead: 'Your choice is a practice plan. It does not change this assessment.',
  license: 'Licence there',
  stop: 'Stop prescribing there',
  fee: 'Published state fee',
  feeSource: 'Read the state fee source',
  feeAsOf: 'Fee schedule read',
  time: 'Typical processing time',
  eligibility: 'Compact eligibility',
  notKnown: 'Not known',
  activity: 'Prescribing activity affected',
  save: 'Save this plan',
  saved: 'Plan saved',
  saving: 'Saving…',
  error: 'That plan did not save. Please try again.',
} as const;

/** What a file's recorded details say about their checking (COHERE5 P0-1): a board
 *  check is a recorded result for a credential, never the mere fact that the
 *  assessment ran over what the file said. */
export function fileStageWords(stage: string | null | undefined): string | null {
  switch (stage) {
    case 'board_checked': return 'Checked against the board';
    case 'in_results': return 'Included in your results';
    case 'details_recorded': return 'Details recorded · waiting for a board check';
    default: return null;
  }
}

/** The compliance-review note when no binder has gone to counsel: sending and the
 *  practice having seen its results are two facts (COHERE5 P1-1). */
export function noBinderSentLine(firstRelease?: string | null): string {
  return firstRelease
    ? 'No binder sent yet.'
    : 'No binder sent yet. The practice has not seen its results yet.';
}


const PRODUCT_DECLARATION_MESSAGES: Readonly<Record<string, string>> = {
  customer_declared_fda_finished_product: 'You confirmed this is an FDA-approved finished product; this product placement is based on your declaration, not FDA verification.',
  customer_declared_compounded_product: 'You confirmed this is a compounded preparation; this product placement is based on your declaration.',
  customer_declared_unapproved_product: 'You confirmed this product is marketed without FDA approval; this product placement is based on your declaration.',
  customer_declared_product_class: 'You chose this medication class; this product placement is based on your declaration, not FDA verification.',
};

export function productDeclarationLabels(codes?: readonly string[] | null): string[] {
  return [...new Set(codes ?? [])].sort().flatMap((code) =>
    Object.prototype.hasOwnProperty.call(PRODUCT_DECLARATION_MESSAGES, code) ? [PRODUCT_DECLARATION_MESSAGES[code]] : [],
  );
}

/** Saved choices use their returned class metadata; no class-name heuristics. */
export function savedProductDeclarationLabel(
  choice: string,
  row?: { fda_status?: string | null; top_level_code?: string | null },
): string | null {
  if (choice !== 'class') return null;
  const code = row?.top_level_code === 'compounded'
    ? 'customer_declared_compounded_product'
    : row?.fda_status === 'approved'
      ? 'customer_declared_fda_finished_product'
      : row?.fda_status === 'unapproved_marketed'
        ? 'customer_declared_unapproved_product'
        : 'customer_declared_product_class';
  return PRODUCT_DECLARATION_MESSAGES[code];
}
